import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define available languages
export type Language = 'en' | 'vi' | 'ko' | 'zh';
// Language names for display
export const languageNames = {
  en: 'English',
  vi: 'Tiếng Việt',
  ko: '한국어',
  zh: '中文',
  tr: 'Türkçe'
};

// Language flags/codes for display
export const languageCodes = {
  en: '🇺🇸',
  vi: '🇻🇳',
  ko: '🇰🇷',
  zh: '🇨🇳',
  tr: '🇹🇷'
};


type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, variables?: Record<string, any>) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Load translations
const loadTranslations = async (lang: Language) => {
  try {
    // In a real implementation, you'd load JSON files for each language
    // For now, we'll use mock translations
    const translations = {
      en: {
        'nav.home': 'Home',
        'nav.cexs': 'CEXs & Core Team',
        'nav.operations': 'Operations',
        'nav.transactions': 'Transactions',
        'nav.blocks': 'Blocks',
        'nav.nodes': 'Nodes',
        'nav.stats': 'Statistics',
        'stats.accounts': 'Account Distribution',
        'stats.unlocks': 'Pi Unlocks 30 Days',
        'stats.unlocks_monthly': 'Pi Unlocks Monthly',
        'stats.domain_auction': 'Domain Auction Stats',
        'stats.largest_tx': 'Largest Transactions',
        'stats.migrate': 'Migration Statistics',
        'search.placeholder': 'Search...',
        // Add more translations as needed
        'operations.self_transfer': 'Self Transfer',
        'operations.domain_auction': 'Domain Auction',
        'operations.claim_claimable_balance': 'Claim π',
        'operations.create_account': 'Create Account',
        'operations.payment': 'Payment',
        'operations.path_payment_strict_receive': 'Path Payment Strict Receive',
        'operations.path_payment_strict_send': 'Path Payment Strict Send',
        'operations.manage_sell_offer': 'Manage Sell Offer',
        'operations.create_passive_sell_offer': 'Create Passive Sell Offer',
        'operations.set_options': 'Set Options',
        'operations.change_trust': 'Change Trust',
        'operations.allow_trust': 'Allow Trust',
        'operations.account_merge': 'Account Merge',
        'operations.inflation': 'Inflation',
        'operations.manage_data': 'Manage Data',
        'operations.bump_sequence': 'Bump Sequence',
        'operations.manage_buy_offer': 'Manage Buy Offer',
        'operations.create_passive_buy_offer': 'Create Passive Buy Offer',
        'operations.create_claimable_balance': 'Create Claimable Balance',
        'operations.claim_claimable_balance_atom': 'Claim Claimable Balance',
        'operations.deposit': 'Deposit',
        'operations.withdraw': 'Withdraw',
        'operations.pay': 'Pay',
        'operations.to': 'to',
        'operations.from': 'from',
        'operations.claimant': 'Claimant',
        'operations.with_starting_balance': 'with starting balance',
        'operations.claim': 'Claim',
        // Operations page translations
        'operations.heading': 'Explore the latest operations on Pi Network blockchain',
        'operations.title': 'Latest Operations on Pi Network',
        'operations.description': 'Explore the latest operations on the Pi Network blockchain.',
        'operations.source_account': 'Source account',
        'operations.operation': 'Operation',
        'operations.amount': 'Amount',
        'operations.tx': 'Tx',
        'operations.time': 'Time',
        // Pagination translations (can be reused across pages)
        'pagination.previous': 'Previous',
        'pagination.next': 'Next',
        'pagination.page': 'Page',
        // Account details page translations
        'account.title': 'Account {{address}} - Pi Network Explorer',
        'account.heading': 'Account Detail',
        'account.heading_with_address': 'Account {{address}}',
        'account.description': 'Details of Account {{address}} on the Pi Network.',
        'account.loading': 'Loading Account Details...',
        'account.not_found': 'Account not found.',
        'account.scam_warning_title': 'Scam Warning',
        'account.scam_warning_description': 'This wallet address has been flagged as unsafe based on community reports. It may be associated with scams, compromised private keys, or unauthorized access. Proceed with caution before making any transactions.',
        'account.overview': 'Overview',
        'account.available_balance': 'Available balance',
        'account.last_modified_time': 'Last modified time',
        'account.locking': 'Locking',
        'account.total_locking': 'Total locking ({{count}} items)',
        'account.next_unlock_time': 'Next unlock time',
        'account.claimable': 'Claimable',
        'account.amount': 'Amount',
        'account.number_of_items': 'Number of items',
        'account.over_100_items': 'over 100 items',
        'account.tx_hash': 'Tx Hash',
        'account.operation_count': 'Operation Count',
        'account.fee': 'Fee',
        'account.created_at': 'Created At',
        'account.account': 'Account',
        'account.effect': 'Effect',
        'account.sponsor': 'Sponsor',
        'account.predicate': 'Predicate',
        'account.create_time': 'Create time',
        'account.latest_100_claimable': 'Latest 100 claimable balances',
        'account.tabs.operations': 'Operations',
        'account.tabs.transactions': 'Transactions',
        'account.tabs.effects': 'Effects',
        'account.tabs.claimable_balances': 'Claimable balances',
        // Effect types (common ones)
        'effects.account_credited': 'Account credited',
        'effects.account_debited': 'Account debited',
        'effects.account_created': 'Account created',
        'effects.claimable_balance_created': 'Claimable balance created',
        'effects.claimable_balance_claimed': 'Claimable balance claimed',
        'effects.claimable_balance_claimant_created': 'Claimable balance claimant created',
        'effects.account_removed': 'Account removed',
        'effects.account_thresholds_updated': 'Account thresholds updated',
        'effects.account_home_domain_updated': 'Account home domain updated',
        'effects.account_flags_updated': 'Account flags updated',
        'effects.account_inflation_destination_updated': 'Account inflation destination updated',
        'effects.signer_created': 'Signer created',
        'effects.signer_removed': 'Signer removed',
        'effects.signer_updated': 'Signer updated',
        'effects.trustline_removed': 'Trustline removed',
        'effects.trustline_updated': 'Trustline updated',
        'effects.trustline_authorized': 'Trustline authorized',
        'effects.trustline_deauthorized': 'Trustline deauthorized',
        'effects.offer_created': 'Offer created',
        'effects.offer_removed': 'Offer removed',
        'effects.offer_updated': 'Offer updated',
        'effects.trade': 'Trade executed',
        'effects.data_created': 'Data created',
        'effects.data_removed': 'Data removed',
        'effects.data_updated': 'Data updated',
        'effects.sequence_bumped': 'Sequence bumped',
        'effects.liquidity_pool_created': 'Liquidity pool created',
        'effects.liquidity_pool_removed': 'Liquidity pool removed',
        'effects.liquidity_pool_deposited': 'Liquidity pool deposited',
        'effects.liquidity_pool_withdrew': 'Liquidity pool withdrew',
        'effects.liquidity_pool_trade': 'Liquidity pool trade',
        'effects.liquidity_pool_revoked': 'Liquidity pool revoked',
        'effects.contract_credited': 'Contract credited',
        'effects.contract_debited': 'Contract debited',
        'effects.claimable_balance_sponsorship_removed': 'Claimable balance sponsorship removed',
        'account_stats.heading': 'Pi Account Statistics - Balance Distribution & Top Accounts',
        'account_stats.title': 'Pi Account Statistics - Balance Distribution & Top Accounts',
        'account_stats.description': 'View the richest Pi accounts and overall balance distribution across the Pi network.',
        'account_stats.intro_text': 'Explore the distribution of Pi across all accounts and view the top Pi holders.',
        'account_stats.distribution_chart_title': 'Pi Holder Distribution',
        'account_stats.categories_title': 'Distribution Categories',
        'account_stats.top_accounts_title': 'Top Pi Accounts',
        'account_stats.accounts': 'accounts',
        'account_stats.of_total': 'of total',
        'account_stats.table.category': 'Category',
        'account_stats.table.balance_range': 'Balance Range',
        'account_stats.table.usd_value': 'USD Value',
        'account_stats.table.accounts': 'Accounts',
        'account_stats.table.percentage': '% of Total',
        'account_stats.table.total': 'Total',
        'account_stats.table.rank': 'Rank',
        'account_stats.table.account': 'Account',
        'account_stats.table.balance': 'Balance',
        'account_stats.table.value': 'Value',
        'account_stats.table.supply_percentage': '% of Supply',
        'account_stats.categories.whales': 'Whales',
        'account_stats.categories.sharks': 'Sharks',
        'account_stats.categories.dolphins': 'Dolphins',
        'account_stats.categories.tunas': 'Tunas',
        'account_stats.categories.fish': 'Fish',
        'account_stats.categories.shrimps': 'Shrimps',
        'account_stats.categories.plankton': 'Plankton',
        'account_stats.categories.microbes': 'Microbes',
        'number.billion_short': 'B',
        'number.million_short': 'M',
        'number.thousand_short': 'K',
        'common.loading': 'Loading...',
        'common.sponsored': 'Sponsored',
        'common.ad': 'Ad',
        'common.ad_not_available': 'Sponsored content not available in your region',

        'blocks.heading': 'Explore the latest blocks on Pi Network blockchain',
        'blocks.title': 'Latest Blocks on Pi Network',
        'blocks.description': 'Explore the latest blocks on the Pi Network blockchain.',
        'blocks.sequence': 'Sequence',
        'blocks.hash': 'Hash',
        'blocks.transactions': 'Transactions',
        'blocks.operations': 'Operations',
        'blocks.time': 'Time',
        'blocks.successful': 'successful',
        'blocks.failed': 'failed',
        'block.title': 'Block #{{sequence}} - Pi Network Explorer',
        'block.heading': 'Block #{{sequence}}',
        'block.description': 'Details of Block #{{sequence}} on the Pi Network blockchain.',
        'block.loading': 'Loading Block Details...',
        'block.not_found': 'Block not found.',
        'block.information': 'Block Information',
        'block.hash': 'Hash',
        'block.previous_hash': 'Previous Hash',
        'block.sequence': 'Sequence',
        'block.time': 'Time',
        'block.total_coins': 'Total Coins',
        'block.fee_pool': 'Fee Pool',
        'block.base_fee': 'Base Fee',
        'block.base_reserve': 'Base Reserve',
        'block.protocol_version': 'Protocol Version',
        'block.tx_hash': 'Tx Hash',
        'block.operation_count': 'Operation Count',
        'block.fee': 'Fee',
        'block.created_at': 'Created At',
        'block.tabs.transactions': 'Transactions',
        'block.tabs.operations': 'Operations', 
        'block.tabs.effects': 'Effects',
        'cex.heading': 'CEX & Core team accounts Statistics',
        'cex.title': 'CEX & Core team accounts on Pi Network',
        'cex.description': 'Explore the CEX & Core team accounts on the Pi Network blockchain.',
        'cex.wallet_balances': 'CEXs wallet balances',
        'cex.core_team_wallets': 'Pi core team wallets',
        'cex.cmc_ranking': 'CMC Ranking',
        'cex.exchange': 'Exchange',
        'cex.chart_link': 'Chart link',
        'cex.signup_link': 'SignUp link',
        'cex.wallet_address': 'Wallet Address',
        'cex.balance_pi': 'Balance (Pi)',
        'cex.balance_usd': 'Balance (USD)',
        'cex.chart': 'Chart',
        'cex.signup': 'SignUp',
        'cex.total': 'Total',
        'cex.note': 'Note',
        'cex.pi_foundation': 'Pi Foundation',
        'cex.total_in': 'Total In (last 24h)',
        'cex.total_out': 'Total Out (last 24h)',
        'cex.net_difference': 'Net Flow (last 24h)',
        'cex.flow_data_updated': 'Flow data last updated (refreshed every 30 mins)',

        'contact.heading': 'Contact Us',
        'contact.title': 'Contact PiScan',
        'contact.description': 'Contact PiScan for any queries or feedback.',
        'contact.intro_text': 'If you have any questions, feel free to reach us at',
        'contact.success_message': 'Thank you for contacting us! We will get back to you soon.',
        'contact.form.name': 'Your Name',
        'contact.form.email': 'Your Email',
        'contact.form.message': 'Message',
        'contact.form.submit': 'Send Message',
        'home.heading': 'Explore Pi Network transactions, wallets, and statistics',
        'home.title': 'Explore Pi Network transactions, wallets, and statistics',
        'home.description': 'Explore Pi Network transactions, wallets, and statistics',
        'home.market_cap': 'Market cap',
        'home.price': 'Price',
        'home.24h_change': '24h%',
        'home.pi_supply': 'Pi Supply',
        'home.circulating': 'Circulating',
        'home.locked': 'Locked',
        'home.cex_balance_total': 'CEX Balance (total)',
        'home.view_all': 'View All',
        'home.network_stats': 'Network Stats',
        'home.blocks': 'Blocks',
        'home.tps': 'TPS',
        'home.latest_transactions': 'Latest Transactions',
        'home.signature': 'Signature',
        'home.time': 'Time',
        'home.block': 'Block',
        'home.ops': 'Ops',
        'home.latest_operations': 'Latest Operations',
        'home.source_account': 'Source account',
        'home.operation': 'Operation',
        'home.amount': 'Amount',
        'home.tx': 'Tx',
        'nodes.heading': 'Pi Network Node Map & Network Statistics',
        'nodes.title': 'Pi Network Nodes & Validators',
        'nodes.description': 'Explore Pi Network nodes, validators, and network topology. View the global distribution and connectivity of nodes.',
        'nodes.active_nodes': 'Active Nodes',
        'nodes.validators': 'Validators',
        'nodes.known_peers': 'Known Peers',
        'nodes.disconnected_nodes': 'Disconnected Nodes',
        'nodes.network_graph': 'Network Graph',
        'nodes.node_types': 'Node Types',
        'nodes.validator': 'Validator',
        'nodes.watcher': 'Watcher',
        'nodes.disconnected_private': 'Disconnected/Private',
        'nodes.click_to_highlight': 'Click on a node to highlight its connections',
        'nodes.global_distribution': 'Global Distribution',
        'nodes.mapped_nodes': 'mapped nodes',
        'nodes.node_details': 'Node Details',
        'nodes.nodes': 'nodes',
        'nodes.address': 'Address',
        'nodes.type': 'Type',
        'nodes.location': 'Location',
        'nodes.isp': 'ISP',
        'nodes.version': 'Version',
        'nodes.trust': 'Trust',
        'nodes.trusted_by': 'Trusted by',
        'nodes.status': 'Status',
        'nodes.status.connected': 'Connected',
        'nodes.status.disconnected': 'Disconnected',
        'nodes.status.private': 'Private',
        'nodes.active_quorum_sets': 'Active Quorum Sets',
        'nodes.active': 'active',
        'nodes.quorum_hash': 'Quorum Hash',
        'nodes.node_id': 'Node ID',
        'nodes.unknown': 'Unknown',
        'nodes.core_team': 'Core Team',
        'nodes.tooltip.node_id': 'Node ID',
        'nodes.tooltip.address': 'Address',
        'nodes.tooltip.type': 'Type',
        'nodes.tooltip.status': 'Status',
        'nodes.tooltip.version': 'Version',
        'nodes.tooltip.location': 'Location',
        'nodes.tooltip.not_accessible': 'Not accessible',
                
        // English translations
        'report_scam.heading': 'Report Scam Wallet',
        'report_scam.title': 'Report Scam Wallet on PiScan',
        'report_scam.description': 'Report a scam wallet address involved in fraudulent or scam activities.',
        'report_scam.intro_text': 'If you have encountered a suspicious wallet address involved in fraudulent or scam activities, please report it using the form below. Our team will review the submission, and if confirmed as a scam, we will mark the wallet as "Scam" on PiScan to warn the community.',
        'report_scam.success_message': 'Thank you for your report! Our team will review it as soon as possible.',
        'report_scam.form.wallet_address': 'Wallet Address',
        'report_scam.form.wallet_placeholder': 'Enter the scam wallet address',
        'report_scam.form.reason': 'Reason for Reporting',
        'report_scam.form.reason_placeholder': 'Describe why this wallet is suspected of scam activities',
        'report_scam.form.email': 'Your Email (Optional)',
        'report_scam.form.email_placeholder': 'Enter your email (optional)',
        'report_scam.form.email_note': 'We may contact you for further details.',
        'report_scam.form.submit': 'Submit Report',

        'transaction.title': 'Transaction #{{id}} on Pi Network',
        'transaction.heading': 'Transaction details',
        'transaction.description': 'Details of transaction #{{id}} on the Pi Network blockchain.',
        'transaction.loading': 'Loading Transaction Details...',
        'transaction.not_found': 'Transaction not found.',
        'transaction.hash': 'Hash',
        'transaction.ledger_block': 'Ledger (Block)',
        'transaction.result': 'Result',
        'transaction.source_account': 'Source account',
        'transaction.fee_account': 'Fee account',
        'transaction.fee': 'Fee',
        'transaction.operation_count': 'Number of Operations',
        'transaction.summary': 'Summary',
        'transaction.operations': 'Operations',
        'transaction.effects': 'Effects',
        'transaction.account': 'Account',
        'transaction.operation': 'Operation',
        'transaction.effect': 'Effect',
        'transaction.amount': 'Amount',
        'transaction.successful': 'Successful',
        'transaction.failed': 'Failed',
        'transaction.loading_details': 'Loading transaction details...',
        'transaction.with_operations': 'Transaction with {{count}} operations',
        'transaction.core_team_distributed': 'Pi Core Team distributed',
        'transaction.to': 'to',
        'transaction.accounts': 'accounts',
        'transaction.locked_for_days': ', locked for {{days}} days',
        'transaction.locked_range_days': ', with lock-up periods ranging from {{minDays}} to {{maxDays}} days',
        'transaction.created_at': 'Created At',

        'unlock_stats.heading': 'Pi Unlock Statistics - Next 30 Days',
        'unlock_stats.title': 'Pi Unlock Statistics - Next 30 Days',
        'unlock_stats.description': 'View the projected Pi cryptocurrency unlock statistics for the next 30 days.',
        'unlock_stats.intro_text': 'Pi unlocks over the next 30 days based on claimable balances—either user-locked or distributed by the Pi Core Team. Unreleased Pi not yet in claimable balances cannot be tracked. Check regularly for updates. All times are in UTC.',
        'unlock_stats.today_note': 'Today\'s unlock amount represents the remaining Pi to be unlocked from now until the end of the day (UTC).',
        'unlock_stats.updated_at': 'Data updated at:',
        'unlock_stats.unknown': 'Unknown',
        'unlock_stats.total_locked': 'Total Locked Pi',
        'unlock_stats.total_next_30': 'Total Unlock in Next 30d',
        'unlock_stats.of_total_locked': 'of total locked',
        'unlock_stats.avg_daily': 'Avg Daily Unlock',
        'unlock_stats.per_day': 'per day',
        'unlock_stats.highest_day': 'Highest Day',
        'unlock_stats.on': 'on',
        'unlock_stats.chart_title': 'Pi Unlock Chart - Next 30 Days',
        'unlock_stats.data_table': 'Unlock Data Table',
        'unlock_stats.date': 'Date',
        'unlock_stats.amount': 'Amount',
        'unlock_stats.value': 'Value',
        'unlock_stats.pi_unlock': 'Pi Unlock',
        'unlock_stats.y_axis_label': 'Pi unlock (in millions)',
        "unlock_stats.monthly_heading": "Monthly Pi Unlock Statistics",
        "unlock_stats.monthly_title": "Monthly Pi Unlock Statistics",
        // Add to LanguageContext.tsx
        "unlock_stats.total_next_12": "Total next 12 months",
        "unlock_stats.monthly_description": "View Pi cryptocurrency unlock statistics by month",
        "unlock_stats.monthly_intro_text": "View Pi cryptocurrency unlock statistics by month.",
        "unlock_stats.monthly_note": "Statistics are based on eligible unlock claims.",
        "unlock_stats.monthly_chart_title": "Pi Unlock Statistics by Month",
        "unlock_stats.total_forecast": "Total Statistics",
        "unlock_stats.monthly_data_table": "Monthly Unlock Data",
        "unlock_stats.total_locking": "Total locking",
        "unlock_stats.avg_monthly": "Avg Monthly",
        "unlock_stats.highest_month": "Highest Month",
        "unlock_stats.per_month": "per month",
        "unlock_stats.in": "in",
        "unlock_stats.month": "Month",
        "unlock_stats.view_daily": "View Daily Unlock Statistics",
        "unlock_stats.view_monthly": "View Monthly Unlock Statistics",

        'common.loading_error': 'Error loading data. Please try again later.',

        'settings.aria_label': 'Open settings',
        'settings.language': 'Language',
        'settings.appearance': 'Appearance',
        'settings.dark_mode': 'Dark mode',
        'settings.light': 'Light',
        'settings.dark': 'Dark',

        "transactios.heading": "Explore the latest transactions on Pi Network blockchain",
        "transactions.title": "Latest Transactions on Pi Network",
        "transactions.description": "Explore the latest transactions on the Pi Network blockchain.",

        
          "domain_auction_stats.heading": "Pi Domain Auction Statistics",
          "domain_auction_stats.title": "Pi Domain Auction Stats | Pi Network Statistics",
          "domain_auction_stats.description": "Analysis of Pi Network domain auctions, showing the distribution of bids across different price ranges and total Pi bid so far.",
          "domain_auction_stats.intro_text": "This page provides an analysis of Pi domain auctions, highlighting bid distributions across different price ranges.",
          "domain_auction_stats.last_updated": "Last updated",
          "domain_auction_stats.summary_title": "Auction Bidding Summary",
          "domain_auction_stats.total_bids": "Total bids placed",
          "domain_auction_stats.total_pi_bid": "Total Pi bid so far",
          "domain_auction_stats.estimated_value": "Estimated value (USD)",
          "domain_auction_stats.pi_price": "Current Pi price",
          "domain_auction_stats.distribution_title": "Bid Distribution",
          "domain_auction_stats.chart_title": "Bid Price Distribution",
          "domain_auction_stats.details_title": "Live Bid Breakdown",
          "domain_auction_stats.bids": "bids",
          "domain_auction_stats.of_total": "of total bids placed",
          "domain_auction_stats.bid_count": "Number of Bids",
          "domain_auction_stats.ranges.low": "Low Bids",
          "domain_auction_stats.ranges.medium": "Standard Bids",
          "domain_auction_stats.ranges.high": "Premium Bids",
          "domain_auction_stats.ranges.premium": "Luxury Bids",
          "domain_auction_stats.ranges.elite": "Elite Bids",
          "domain_auction_stats.table.price_range": "Bid Price Range",
          "domain_auction_stats.table.bid_count": "Number of Bids",
          "domain_auction_stats.table.percentage": "Percentage",
          "domain_auction_stats.table.total": "Total Bids",
          "domain_auction_stats.highest_bid": "Highest Bid",
          "domain_auction_stats.top_bids_title": "Top Bids",
          "domain_auction_stats.table.account": "Account",
          "domain_auction_stats.table.bid_amount": "Bid Amount",
          "domain_auction_stats.table.usd_value": "USD Value",
          
          "largest_tx.heading": "Largest Pi Transactions (Last 24h)",
          "largest_tx.title": "Largest Pi Transactions in the Last 24 Hours - PiScan",
          "largest_tx.description": "Explore the largest Pi transactions on the Pi Network blockchain in the past 24 hours.",
          "largest_tx.list_heading": "Top Transactions by Amount (Last 24h)",
          "largest_tx.rank": "Rank",
          "largest_tx.transaction": "Transaction",
          "largest_tx.account": "Account",
          "largest_tx.amount": "Amount (π)",
          "largest_tx.date": "Date & Time",
          "largest_tx.no_data": "No transaction data available in the last 24 hours.",
          "largest_tx.fetch_error": "Failed to fetch transaction data.",
          "largest_tx.loading": "Loading largest transactions...",
          "largest_tx.largest_amount": "Largest Transaction Amount (Last 24h)",
          "largest_tx.latest_large_tx": "Latest Large Transaction (Last 24h)",
          "largest_tx.total_value": "Total Value (Top 100 Transactions in Last 24h)",
        
          "migrate_stats.heading": "Pi Migration Statistics",
          "migrate_stats.title": "Pi Migration to Mainnet Statistics - PiScan",
          "migrate_stats.description": "Track daily Pi migration to mainnet statistics for recent days.",
          "migrate_stats.last_updated": "Last Updated",
        
          "migrate_stats.daily_migration": "Daily Migration Volume",
          "migrate_stats.daily_stats": "Migration Statistics by Day",
          "migrate_stats.loading": "Loading migration statistics...",
          "migrate_stats.no_data": "No migration data available.",
          "migrate_stats.fetch_error": "Failed to fetch migration data.",
          "migrate_stats.date": "Date",
          "migrate_stats.amount": "Amount (π)",
          "migrate_stats.percentage": "Percentage",
          "migrate_stats.total": "Total",
          "migrate_stats.pi_migrated": "Total Pi Migrated",
          "migrate_stats.date_label": "Date: {{date}}",
          "migrate_stats.usd_value": "USD Value",
          "migrate_stats.at_price": "at the current price of",
          "migrate_stats.current_price_note": "USD values calculated at the current Pi price of {{price}} per π.",
                  

      },
      vi: {
        "nav.home": "Trang Chủ",
        "nav.cexs": "Sàn & Core Team",
        "nav.operations": "Thao tác",
        "nav.transactions": "Giao Dịch",
        "nav.blocks": "Khối",
        "nav.nodes": "Các Node",
        "nav.stats": "Thống Kê",
        "stats.accounts": "Phân Bố Tài Khoản",
        "stats.unlocks": "Mở Khóa Pi 30 Ngày",
        "stats.unlocks_monthly": "Mở Khóa Pi Hàng Tháng",
        'stats.domain_auction': 'Thống kê Đấu giá Tên miền',
        'stats.largest_tx': 'Giao Dịch Lớn Nhất',
        'stats.migrate': 'Thống kê di chuyển mainnet',
        "search.placeholder": "Tìm kiếm...",
        'operations.self_transfer': 'Tự Chuyển',
        'operations.domain_auction': 'Đấu Giá Tên Miền',
        "operations.claim_claimable_balance": "Nhận π",
        "operations.create_account": "Tạo Tài Khoản",
        "operations.payment": "Thanh Toán",
        "operations.path_payment_strict_receive": "Thanh Toán Đường Dẫn Nhận Nghiêm Ngặt",
        "operations.path_payment_strict_send": "Thanh Toán Đường Dẫn Gửi Nghiêm Ngặt",
        "operations.manage_sell_offer": "Quản Lý Đề Xuất Bán",
        "operations.create_passive_sell_offer": "Tạo Đề Xuất Bán Thụ Động",
        "operations.set_options": "Đặt Tùy Chọn",
        "operations.change_trust": "Thay Đổi Tin Cậy",
        "operations.allow_trust": "Cho Phép Tin Cậy",
        "operations.account_merge": "Hợp Nhất Tài Khoản",
        "operations.inflation": "Lạm Phát",
        "operations.manage_data": "Quản Lý Dữ Liệu",
        "operations.bump_sequence": "Tăng Chuỗi",
        "operations.manage_buy_offer": "Quản Lý Đề Xuất Mua",
        "operations.create_passive_buy_offer": "Tạo Đề Xuất Mua Thụ Động",
        "operations.create_claimable_balance": "Tạo Số Dư Có Thể Nhận",
        "operations.claim_claimable_balance_atom": "Nhận Số Dư",
        "operations.deposit": "Nạp",
        "operations.withdraw": "Rút",
        "operations.pay": "Thanh toán",
        "operations.to": "đến",
        "operations.from": "từ",
        "operations.claimant": "Người nhận",
        "operations.with_starting_balance": "với số dư ban đầu",
        "operations.claim": "Nhận",
        "operations.heading": "Khám phá các thao tác mới nhất trên blockchain Pi Network",
        "operations.title": "Các thao tác mới nhất trên Pi Network",
        "operations.description": "Khám phá các thao tác mới nhất trên blockchain Pi Network.",
        "operations.source_account": "Tài khoản nguồn",
        "operations.operation": "Thao tác",
        "operations.amount": "Số lượng",
        "operations.tx": "Giao dịch",
        "operations.time": "Thời gian",
        "pagination.previous": "Trước",
        "pagination.next": "Tiếp",
        "pagination.page": "Trang",
        "account.title": "Tài khoản {{address}} - Pi Network Explorer",
        "account.heading": "Chi tiết Tài Khoản",
        "account.heading_with_address": "Tài khoản {{address}}",
        "account.description": "Chi tiết của Tài khoản {{address}} trên mạng Pi Network.",
        "account.loading": "Đang tải chi tiết tài khoản...",
        "account.not_found": "Không tìm thấy tài khoản.",
        "account.scam_warning_title": "Cảnh báo lừa đảo",
        "account.scam_warning_description": "Địa chỉ ví này đã bị đánh dấu là không an toàn dựa trên báo cáo của cộng đồng. Nó có thể liên quan đến lừa đảo, khóa riêng tư bị xâm phạm hoặc truy cập trái phép. Hãy thận trọng trước khi thực hiện bất kỳ giao dịch nào.",
        "account.overview": "Tổng quan",
        "account.available_balance": "Số dư khả dụng",
        "account.last_modified_time": "Thời gian sửa đổi cuối",
        "account.locking": "Đang khóa",
        "account.total_locking": "Tổng số khóa ({{count}} mục)",
        "account.next_unlock_time": "Thời gian mở khóa tiếp theo",
        "account.claimable": "Có thể nhận",
        "account.amount": "Số lượng",
        "account.number_of_items": "Số lượng mục",
        "account.over_100_items": "hơn 100 mục",
        "account.tx_hash": "Mã giao dịch",
        "account.operation_count": "Số lượng thao tác",
        "account.fee": "Phí",
        "account.created_at": "Tạo lúc",
        "account.account": "Tài khoản",
        "account.effect": "Tác động",
        "account.sponsor": "Nhà tài trợ",
        "account.predicate": "Điều kiện",
        "account.create_time": "Thời gian tạo",
        "account.latest_100_claimable": "100 số dư có thể nhận mới nhất",
        "account.tabs.operations": "Các thao tác",
        "account.tabs.transactions": "Giao dịch",
        "account.tabs.effects": "Tác động",
        "account.tabs.claimable_balances": "Số dư có thể nhận",
        "effects.account_credited": "Tài khoản được ghi có",
        "effects.account_debited": "Tài khoản bị ghi nợ",
        "effects.account_created": "Tài khoản được tạo",
        "effects.claimable_balance_created": "Số dư có thể nhận đã được tạo",
        "effects.claimable_balance_claimed": "Số dư có thể nhận đã được nhận",
        "effects.claimable_balance_claimant_created": "Người nhận số dư đã được tạo",
        "effects.account_removed": "Tài khoản bị xóa",
        "effects.account_thresholds_updated": "Ngưỡng tài khoản được cập nhật",
        "effects.account_home_domain_updated": "Tên miền chính của tài khoản được cập nhật",
        "effects.account_flags_updated": "Cờ tài khoản được cập nhật",
        "effects.account_inflation_destination_updated": "Điểm đến lạm phát tài khoản được cập nhật",
        "effects.signer_created": "Người ký được tạo",
        "effects.signer_removed": "Người ký bị xóa",
        "effects.signer_updated": "Người ký được cập nhật",
        "effects.trustline_created": "Đường tin cậy được tạo",
        "effects.trustline_removed": "Đường tin cậy bị xóa",
        "effects.trustline_updated": "Đường tin cậy được cập nhật",
        "effects.trustline_authorized": "Đường tin cậy được ủy quyền",
        "effects.trustline_deauthorized": "Đường tin cậy bị hủy ủy quyền",
        "effects.offer_created": "Đề xuất được tạo",
        "effects.offer_removed": "Đề xuất bị xóa",
        "effects.offer_updated": "Đề xuất được cập nhật",
        "effects.trade": "Giao dịch được thực hiện",
        "effects.data_created": "Dữ liệu được tạo",
        "effects.data_removed": "Dữ liệu bị xóa",
        "effects.data_updated": "Dữ liệu được cập nhật",
        "effects.sequence_bumped": "Chuỗi được nâng",
        "effects.liquidity_pool_created": "Pool thanh khoản được tạo",
        "effects.liquidity_pool_removed": "Pool thanh khoản bị xóa",
        "effects.liquidity_pool_deposited": "Pool thanh khoản được gửi tiền",
        "effects.liquidity_pool_withdrew": "Rút tiền từ pool thanh khoản",
        "effects.liquidity_pool_trade": "Giao dịch pool thanh khoản",
        "effects.liquidity_pool_revoked": "Pool thanh khoản bị thu hồi",
        "effects.contract_credited": "Hợp đồng được ghi có",
        "effects.contract_debited": "Hợp đồng bị ghi nợ",
        "effects.claimable_balance_sponsorship_removed": "Tài trợ số dư có thể nhận đã bị xóa",
        "account_stats.heading": "Thống Kê Tài Khoản Pi - Phân Phối Số Dư & Tài Khoản Hàng Đầu",
        "account_stats.title": "Thống Kê Tài Khoản Pi - Phân Phối Số Dư & Tài Khoản Hàng Đầu",
        "account_stats.description": "Xem các tài khoản Pi giàu nhất và phân phối số dư tổng thể trên mạng Pi.",
        "account_stats.intro_text": "Khám phá sự phân phối của Pi trên tất cả các tài khoản và xem những người nắm giữ Pi hàng đầu.",
        "account_stats.distribution_chart_title": "Phân Phối Người Nắm Giữ Pi",
        "account_stats.categories_title": "Danh Mục Phân Phối",
        "account_stats.top_accounts_title": "Tài Khoản Pi Hàng Đầu",
        "account_stats.accounts": "tài khoản",
        "account_stats.of_total": "của tổng số",
        "account_stats.table.category": "Danh mục",
        "account_stats.table.balance_range": "Phạm vi số dư",
        "account_stats.table.usd_value": "Giá trị USD",
        "account_stats.table.accounts": "Tài khoản",
        "account_stats.table.percentage": "% tổng số",
        "account_stats.table.total": "Tổng cộng",
        "account_stats.table.rank": "Hạng",
        "account_stats.table.account": "Tài khoản",
        "account_stats.table.balance": "Số dư",
        "account_stats.table.value": "Giá trị",
        "account_stats.table.supply_percentage": "% lượng cung",
        "account_stats.categories.whales": "Cá Voi",
        "account_stats.categories.sharks": "Cá Mập",
        "account_stats.categories.dolphins": "Cá Heo",
        "account_stats.categories.tunas": "Cá Ngừ",
        "account_stats.categories.fish": "Cá",
        "account_stats.categories.shrimps": "Tôm",
        "account_stats.categories.plankton": "Sinh Vật Phù Du",
        "account_stats.categories.microbes": "Vi Sinh Vật",
        "number.billion_short": "Tỷ",
        "number.million_short": "Tr",
        "number.thousand_short": "N",
        "common.loading": "Đang tải...",
        "common.sponsored": "Tài trợ",
        "common.ad": "Quảng cáo",
        "common.ad_not_available": "Nội dung được tài trợ không có sẵn trong khu vực của bạn",
        "blocks.heading": "Khám phá các khối mới nhất trên blockchain Pi Network",
        "blocks.title": "Các Khối Mới Nhất trên Pi Network",
        "blocks.description": "Khám phá các khối mới nhất trên blockchain Pi Network.",
        "blocks.sequence": "Chuỗi",
        "blocks.hash": "Mã băm",
        "blocks.transactions": "Giao dịch",
        "blocks.operations": "Thao tác",
        "blocks.time": "Thời gian",
        "blocks.successful": "thành công",
        "blocks.failed": "thất bại",
        "block.title": "Khối #{{sequence}} - Pi Network Explorer",
        "block.heading": "Khối #{{sequence}}",
        "block.description": "Chi tiết của Khối #{{sequence}} trên mạng lưới Pi Network.",
        "block.loading": "Đang tải chi tiết khối...",
        "block.not_found": "Không tìm thấy khối.",
        "block.information": "Thông tin khối",
        "block.hash": "Mã băm",
        "block.previous_hash": "Mã băm trước đó",
        "block.sequence": "Chuỗi",
        "block.time": "Thời gian",
        "block.total_coins": "Tổng số coin",
        "block.fee_pool": "Quỹ phí",
        "block.base_fee": "Phí cơ bản",
        "block.base_reserve": "Dự trữ cơ bản",
        "block.protocol_version": "Phiên bản giao thức",
        "block.tx_hash": "Mã giao dịch",
        "block.operation_count": "Số lượng thao tác",
        "block.fee": "Phí",
        "block.created_at": "Tạo lúc",
        "block.tabs.transactions": "Giao dịch",
        "block.tabs.operations": "Thao tác",
        "block.tabs.effects": "Tác động",
        "cex.heading": "Thống kê tài khoản sàn giao dịch & Đội ngũ lõi",
        "cex.title": "Tài khoản CEX & Đội ngũ lõi trên Pi Network",
        "cex.description": "Khám phá tài khoản CEX & Đội ngũ lõi trên blockchain Pi Network.",
        "cex.wallet_balances": "Số dư ví của các CEX",
        "cex.core_team_wallets": "Ví của đội ngũ lõi Pi",
        "cex.cmc_ranking": "Xếp hạng CMC",
        "cex.exchange": "Sàn giao dịch",
        "cex.chart_link": "Link biểu đồ",
        "cex.signup_link": "Link đăng ký",
        "cex.wallet_address": "Địa chỉ ví",
        "cex.balance_pi": "Số dư (Pi)",
        "cex.balance_usd": "Số dư (USD)",
        "cex.chart": "Biểu đồ",
        "cex.signup": "Đăng ký",
        "cex.total": "Tổng cộng",
        "cex.note": "Ghi chú",
        "cex.pi_foundation": "Pi Foundation",
        "cex.team_wallet_footnote": "Có khoảng 10.000 tài khoản được gắn nhãn thuộc về Đội ngũ lõi Pi không được liệt kê ở đây do số lượng lớn và rất ít giao dịch. Mỗi ví chứa khoảng 2.000.000 Pi, tổng cộng khoảng 20 tỷ Pi chưa được tính đến",
       'cex.total_in': 'Tiền vào (24h gần nhất)',
'cex.total_out': 'Tiền ra (24h gần nhất)',
'cex.net_difference': 'Dòng tiền ròng (24h gần nhất)',
'cex.flow_data_updated': 'Dữ liệu cập nhật mỗi 30 phút',


        "contact.heading": "Liên Hệ Với Chúng Tôi",
        "contact.title": "Liên Hệ PiScan",
        "contact.description": "Liên hệ PiScan cho bất kỳ câu hỏi hoặc phản hồi nào.",
        "contact.intro_text": "Nếu bạn có bất kỳ câu hỏi nào, hãy liên hệ với chúng tôi tại",
        "contact.success_message": "Cảm ơn bạn đã liên hệ với chúng tôi! Chúng tôi sẽ phản hồi sớm.",
        "contact.form.name": "Tên Của Bạn",
        "contact.form.email": "Email Của Bạn",
        "contact.form.message": "Tin Nhắn",
        "contact.form.submit": "Gửi Tin Nhắn",
        "home.heading": "Khám phá giao dịch, ví và thống kê Mạng Pi",
        "home.title": "Khám phá giao dịch, ví và thống kê Mạng Pi",
        "home.description": "Khám phá giao dịch, ví và thống kê trên Mạng Pi",
        "home.market_cap": "Vốn hoá thị trường",
        "home.price": "Giá",
        "home.24h_change": "24h%",
        "home.pi_supply": "Tổng cung",
        "home.circulating": "Đang lưu hành",
        "home.locked": "Đang khóa",
        "home.cex_balance_total": "Số dư CEX (tổng)",
        "home.view_all": "Xem tất cả",
        "home.network_stats": "Thống kê mạng",
        "home.blocks": "Các khối",
        "home.tps": "GD/giây",
        "home.latest_transactions": "Giao dịch mới nhất",
        "home.signature": "Chữ ký",
        "home.time": "Thời gian",
        "home.block": "Khối",
        "home.ops": "Thao tác",
        "home.latest_operations": "Thao tác mới nhất",
        "home.source_account": "Tài khoản nguồn",
        "home.operation": "Thao tác",
        "home.amount": "Số lượng",
        "home.tx": "Giao dịch",
        "nodes.heading": "Bản Đồ Node & Thống Kê Mạng Pi Network",
        "nodes.title": "Các Node & Validator Của Pi Network",
        "nodes.description": "Khám phá các node, validator và cấu trúc liên kết mạng Pi Network. Xem phân bố và kết nối toàn cầu của các node.",
        "nodes.active_nodes": "Node Hoạt Động",
        "nodes.validators": "Validator",
        "nodes.known_peers": "Các peer đã biết",
        "nodes.disconnected_nodes": "Node Mất Kết Nối",
        "nodes.network_graph": "Biểu Đồ Mạng",
        "nodes.node_types": "Loại Node",
        "nodes.validator": "Validator",
        "nodes.watcher": "Watcher",
        "nodes.disconnected_private": "Mất Kết Nối/Riêng Tư",
        "nodes.click_to_highlight": "Nhấp vào node để làm nổi bật các kết nối của nó",
        "nodes.global_distribution": "Phân Bố Toàn Cầu",
        "nodes.mapped_nodes": "Các node đã được lập bản đồ",
        "nodes.node_details": "Chi Tiết Node",
        "nodes.nodes": "Node",
        "nodes.address": "Địa chỉ",
        "nodes.type": "Loại",
        "nodes.location": "Vị trí",
        "nodes.isp": "Nhà cung cấp",
        "nodes.version": "Phiên bản",
        "nodes.trust": "Tin cậy",
        "nodes.trusted_by": "Được tin cậy bởi",
        "nodes.status": "Trạng thái",
        "nodes.status.connected": "Đã kết nối",
        "nodes.status.disconnected": "Mất kết nối",
        "nodes.status.private": "Riêng tư",
        "nodes.active_quorum_sets": "Các bộ quorum đang hoạt động",
        "nodes.active": "hoạt động",
        "nodes.quorum_hash": "Mã băm số phiếu",
        "nodes.node_id": "ID Node",
        "nodes.unknown": "Không xác định",
        "nodes.core_team": "Đội ngũ lõi",
        "nodes.tooltip.node_id": "ID Node",
        "nodes.tooltip.address": "Địa chỉ",
        "nodes.tooltip.type": "Loại",
        "nodes.tooltip.status": "Trạng thái",
        "nodes.tooltip.version": "Phiên bản",
        "nodes.tooltip.location": "Vị trí",
        "nodes.tooltip.not_accessible": "Không thể truy cập",
        "report_scam.heading": "Báo Cáo Ví Lừa Đảo",
        "report_scam.title": "Báo Cáo Ví Lừa Đảo Trên PiScan",
        "report_scam.description": "Báo cáo địa chỉ ví liên quan đến hoạt động lừa đảo hoặc gian lận.",
        "report_scam.intro_text": "Nếu bạn đã gặp phải địa chỉ ví đáng ngờ liên quan đến các hoạt động gian lận hoặc lừa đảo, vui lòng báo cáo bằng biểu mẫu dưới đây. Đội ngũ của chúng tôi sẽ xem xét báo cáo và nếu xác nhận là lừa đảo, chúng tôi sẽ đánh dấu ví là \"Lừa đảo\" trên PiScan để cảnh báo cộng đồng.",
        "report_scam.success_message": "Cảm ơn báo cáo của bạn! Đội ngũ của chúng tôi sẽ xem xét nhanh nhất có thể.",
        "report_scam.form.wallet_address": "Địa Chỉ Ví",
        "report_scam.form.wallet_placeholder": "Nhập địa chỉ ví lừa đảo",
        "report_scam.form.reason": "Lý Do Báo Cáo",
        "report_scam.form.reason_placeholder": "Mô tả lý do ví này bị nghi ngờ có hoạt động lừa đảo",
        "report_scam.form.email": "Email Của Bạn (Tùy Chọn)",
        "report_scam.form.email_placeholder": "Nhập email của bạn (tùy chọn)",
        "report_scam.form.email_note": "Chúng tôi có thể liên hệ với bạn để biết thêm chi tiết.",
        "report_scam.form.submit": "Gửi Báo Cáo",
        "transaction.title": "Giao dịch #{{id}} trên Pi Network",
        "transaction.heading": "Chi tiết giao dịch",
        "transaction.description": "Chi tiết của giao dịch #{{id}} trên blockchain Pi Network.",
        "transaction.loading": "Đang tải chi tiết giao dịch...",
        "transaction.not_found": "Không tìm thấy giao dịch.",
        "transaction.hash": "Mã băm",
        "transaction.ledger_block": "Sổ cái (Khối)",
        "transaction.result": "Kết quả",
        "transaction.source_account": "Tài khoản nguồn",
        "transaction.fee_account": "Tài khoản phí",
        "transaction.fee": "Phí",
        "transaction.operation_count": "Số lượng thao tác",
        "transaction.summary": "Tóm tắt",
        "transaction.operations": "Thao tác",
        "transaction.effects": "Hiệu ứng",
        "transaction.account": "Tài khoản",
        "transaction.operation": "Thao tác",
        "transaction.effect": "Hiệu ứng",
        "transaction.amount": "Số lượng",
        "transaction.successful": "Thành công",
        "transaction.failed": "Thất bại",
        "transaction.loading_details": "Đang tải chi tiết giao dịch...",
        "transaction.with_operations": "Giao dịch với {{count}} thao tác",
        "transaction.core_team_distributed": "Đội ngũ lõi Pi đã phân phối",
        "transaction.to": "cho",
        "transaction.accounts": "tài khoản",
        "transaction.locked_for_days": ", khóa trong {{days}} ngày",
        "transaction.locked_range_days": ", với thời gian khóa từ {{minDays}} đến {{maxDays}} ngày",
        "transaction.created_at": "Thời gian tạo",
        "unlock_stats.heading": "Thống Kê Mở Khóa Pi - 30 Ngày Tới",
        "unlock_stats.title": "Thống Kê Mở Khóa Pi - 30 Ngày Tới",
        "unlock_stats.description": "Xem thống kê dự kiến mở khóa tiền điện tử Pi trong 30 ngày tới.",
        "unlock_stats.intro_text": "Mở khóa Pi trong 30 ngày tới dựa trên số dư có thể nhận—do người dùng khóa hoặc do Đội ngũ Pi Core phân phối. Pi chưa phát hành trong số dư có thể nhận không thể theo dõi được. Kiểm tra thường xuyên để cập nhật. Tất cả thời gian là UTC.",
        "unlock_stats.today_note": "Số lượng mở khóa hôm nay đại diện cho Pi còn lại sẽ được mở khóa từ bây giờ cho đến cuối ngày (UTC).",
        "unlock_stats.updated_at": "Dữ liệu cập nhật vào:",
        "unlock_stats.unknown": "Không xác định",
        "unlock_stats.total_locked": "Tổng Pi Đang Khóa",
        "unlock_stats.total_next_30": "Tổng Mở Khóa 30 Ngày Tới",
        "unlock_stats.of_total_locked": "của tổng khóa",
        "unlock_stats.avg_daily": "Mở Khóa Trung Bình Hàng Ngày",
        "unlock_stats.per_day": "mỗi ngày",
        "unlock_stats.highest_day": "Ngày Cao Nhất",
        "unlock_stats.on": "vào",
        "unlock_stats.chart_title": "Biểu Đồ Mở Khóa Pi - 30 Ngày Tới",
        "unlock_stats.data_table": "Bảng Dữ Liệu Mở Khóa",
        "unlock_stats.date": "Ngày",
        "unlock_stats.amount": "Số lượng",
        "unlock_stats.value": "Giá trị",
        "unlock_stats.pi_unlock": "Mở Khóa Pi",
        "unlock_stats.y_axis_label": "Mở khóa Pi (theo triệu)",
        "unlock_stats.monthly_heading": "Thống kê mở khóa Pi hàng tháng",
        "unlock_stats.monthly_title": "Thống kê mở khóa Pi hàng tháng",
        "unlock_stats.total_next_12": "Tổng 12 tháng tới",
        "unlock_stats.monthly_description": "Xem thống kê mở khóa tiền điện tử Pi theo tháng",
        "unlock_stats.monthly_intro_text": "Xem thống kê mở khóa tiền điện tử Pi theo tháng.",
        "unlock_stats.monthly_note": "Các thống kê dựa trên yêu cầu mở khóa hợp lệ.",
        "unlock_stats.monthly_chart_title": "Thống kê mở khóa Pi theo tháng",
        "unlock_stats.total_forecast": "Tổng thống kê",
        "unlock_stats.monthly_data_table": "Dữ liệu mở khóa hàng tháng",
        "unlock_stats.total_locking": "Tổng lượng khóa",
        "unlock_stats.avg_monthly": "Trung bình hàng tháng",
        "unlock_stats.highest_month": "Tháng cao nhất",
        "unlock_stats.per_month": "mỗi tháng",
        "unlock_stats.in": "trong",
        "unlock_stats.month": "Tháng",
        "unlock_stats.view_daily": "Xem thống kê mở khóa hàng ngày",
        "unlock_stats.view_monthly": "Xem thống kê mở khóa hàng tháng",
        "common.loading_error": "Lỗi khi tải dữ liệu. Vui lòng thử lại sau.",
        'settings.aria_label': 'Mở cài đặt',
        'settings.language': 'Ngôn ngữ',
        'settings.appearance': 'Giao diện',
        'settings.dark_mode': 'Chế độ tối',
        'settings.light': 'Sáng',
        'settings.dark': 'Tối',

        "transactios.heading": "Khám phá các giao dịch mới nhất trên blockchain Pi Network",
        "transactions.title": "Các Giao Dịch Mới Nhất trên Pi Network",
        "transactions.description": "Khám phá các giao dịch mới nhất trên blockchain Pi Network.",
        "domain_auction_stats.heading": "Thống kê Đấu giá Tên miền Pi",
    "domain_auction_stats.title": "Thống kê Đấu giá Tên miền Pi | Thống kê Pi Network",
    "domain_auction_stats.description": "Phân tích các phiên đấu giá tên miền trên Pi Network, hiển thị sự phân bố các giá thầu theo các mức giá khác nhau và tổng số Pi đã được đặt giá.",
    "domain_auction_stats.intro_text": "Trang này cung cấp phân tích về các phiên đấu giá tên miền Pi, làm nổi bật sự phân bố của các giá thầu theo các mức giá khác nhau.",
    "domain_auction_stats.last_updated": "Cập nhật lần cuối",
    "domain_auction_stats.summary_title": "Tóm tắt Giá thầu Đấu giá",
    "domain_auction_stats.total_bids": "Tổng số giá thầu",
    "domain_auction_stats.total_pi_bid": "Tổng số Pi đã đặt giá",
    "domain_auction_stats.estimated_value": "Giá trị ước tính (USD)",
    "domain_auction_stats.pi_price": "Giá Pi hiện tại",
    "domain_auction_stats.distribution_title": "Phân bố Giá thầu",
    "domain_auction_stats.chart_title": "Phân bố Mức giá thầu",
    "domain_auction_stats.details_title": "Chi tiết Giá thầu Trực tiếp",
    "domain_auction_stats.bids": "giá thầu",
    "domain_auction_stats.of_total": "trong tổng số giá thầu",
    "domain_auction_stats.bid_count": "Số lượng Giá thầu",
    "domain_auction_stats.ranges.low": "Giá thầu Thấp",
    "domain_auction_stats.ranges.medium": "Giá thầu Trung bình",
    "domain_auction_stats.ranges.high": "Giá thầu Cao cấp",
    "domain_auction_stats.ranges.premium": "Giá thầu Sang trọng",
    "domain_auction_stats.ranges.elite": "Giá thầu Đặc biệt",
    "domain_auction_stats.table.price_range": "Khoảng Giá thầu",
    "domain_auction_stats.table.bid_count": "Số lượng Giá thầu",
    "domain_auction_stats.table.percentage": "Phần trăm",
    "domain_auction_stats.table.total": "Tổng số Giá thầu",
    "domain_auction_stats.highest_bid": "Giá thầu Cao nhất",
    "domain_auction_stats.top_bids_title": "Các Giá thầu Hàng đầu",
    "domain_auction_stats.table.account": "Tài khoản",
    "domain_auction_stats.table.bid_amount": "Giá thầu",
    "domain_auction_stats.table.usd_value": "Giá trị (USD)",
    "largest_tx.heading": "Giao dịch Pi lớn nhất (24h qua)",
  "largest_tx.title": "Giao dịch Pi lớn nhất trong 24 giờ qua - PiScan",
  "largest_tx.description": "Khám phá các giao dịch Pi lớn nhất trên blockchain Pi Network trong 24 giờ qua.",
  "largest_tx.list_heading": "Top giao dịch theo số lượng (24h qua)",
  "largest_tx.rank": "Hạng",
  "largest_tx.transaction": "Giao dịch",
  "largest_tx.account": "Tài khoản",
  "largest_tx.amount": "Số lượng (π)",
  "largest_tx.date": "Ngày & Giờ",
  "largest_tx.no_data": "Không có dữ liệu giao dịch nào trong 24 giờ qua.",
  "largest_tx.fetch_error": "Không thể tải dữ liệu giao dịch.",
  "largest_tx.loading": "Đang tải các giao dịch lớn nhất...",
  "largest_tx.largest_amount": "Giao dịch có số lượng lớn nhất (24h qua)",
  "largest_tx.latest_large_tx": "Giao dịch lớn gần nhất (24h qua)",
  "largest_tx.total_value": "Tổng giá trị (Top 100 giao dịch trong 24h qua)",

  "migrate_stats.heading": "Thống kê di chuyển Pi",
  "migrate_stats.title": "Thống kê di chuyển Pi sang Mainnet - PiScan",
  "migrate_stats.description": "Theo dõi thống kê di chuyển Pi hàng ngày sang Mainnet trong những ngày gần đây.",
  "migrate_stats.last_updated": "Cập nhật lần cuối",

  "migrate_stats.daily_migration": "Lượng Pi di chuyển mỗi ngày",
  "migrate_stats.daily_stats": "Thống kê di chuyển theo ngày",
  "migrate_stats.loading": "Đang tải thống kê di chuyển...",
  "migrate_stats.no_data": "Không có dữ liệu di chuyển.",
  "migrate_stats.fetch_error": "Không thể tải dữ liệu di chuyển.",
  "migrate_stats.date": "Ngày",
  "migrate_stats.amount": "Số lượng (π)",
  "migrate_stats.percentage": "Tỷ lệ (%)",
  "migrate_stats.total": "Tổng cộng",
  "migrate_stats.pi_migrated": "Tổng số Pi đã di chuyển",
  "migrate_stats.date_label": "Ngày: {{date}}",
  "migrate_stats.usd_value": "Giá trị USD",
  "migrate_stats.at_price": "theo giá hiện tại là",
  "migrate_stats.current_price_note": "Giá trị USD được tính theo giá Pi hiện tại là {{price}} mỗi π."
      },
      ko: {
        "nav.home": "홈",
        "nav.cexs": "CEXs 및 코어 팀",
        "nav.operations": "작업",
        "nav.transactions": "거래",
        "nav.blocks": "블록",
        "nav.nodes": "노드",
        "nav.stats": "통계",
        "stats.accounts": "계정 분포",
        "stats.unlocks": "Pi 잠금 해제 통계",
        "stats.unlocks_monthly": "월간 Pi 잠금 해제 통계",
        'stats.domain_auction': 'Pi 도메인 경 매 통계',
        "stats.largest_tx": "최대 거래",
        "stats.migrate": "메인넷 이동 통계",
        "nav.report_scam": "사기 신고",
        "search.placeholder": "검색...",
        'operations.self_transfer': '자체 송금',
        'operations.domain_auction': '도메인 경매',
        "operations.claim_claimable_balance": "π 수령",
        "operations.create_account": "계정 생성",
        "operations.payment": "결제",
        "operations.path_payment_strict_receive": "엄격한 수신 경로 결제",
        "operations.path_payment_strict_send": "엄격한 송신 경로 결제",
        "operations.manage_sell_offer": "판매 제안 관리",
        "operations.create_passive_sell_offer": "수동 판매 제안 생성",
        "operations.set_options": "옵션 설정",
        "operations.change_trust": "신뢰 변경",
        "operations.allow_trust": "신뢰 허용",
        "operations.account_merge": "계정 병합",
        "operations.inflation": "인플레이션",
        "operations.manage_data": "데이터 관리",
        "operations.bump_sequence": "시퀀스 증가",
        "operations.manage_buy_offer": "구매 제안 관리",
        "operations.create_passive_buy_offer": "수동 구매 제안 생성",
        "operations.create_claimable_balance": "수령 가능 잔액 생성",
        "operations.claim_claimable_balance_atom": "수령 가능 잔액 수령",
        "operations.deposit": "입금",
        "operations.withdraw": "출금",
        "operations.pay": "지불",
        "operations.to": "대상",
        "operations.from": "출발",
        "operations.claimant": "수령자",
        "operations.with_starting_balance": "초기 잔액 포함",
        "operations.claim": "수령",
        "operations.heading": "Pi 네트워크 블록체인의 최신 작업 탐색",
        "operations.title": "Pi 네트워크의 최신 작업",
        "operations.description": "Pi 네트워크 블록체인의 최신 작업을 탐색하세요.",
        "operations.source_account": "소스 계정",
        "operations.operation": "작업",
        "operations.amount": "금액",
        "operations.tx": "거래",
        "operations.time": "시간",
        "pagination.previous": "이전",
        "pagination.next": "다음",
        "pagination.page": "페이지",
        "account.title": "계정 {{address}} - Pi 네트워크 탐색기",
        "account.heading": "계정 상세정보",
        "account.heading_with_address": "계정 {{address}}",
        "account.description": "Pi 네트워크의 계정 {{address}} 상세정보입니다.",
        "account.loading": "계정 상세정보 로딩 중...",
        "account.not_found": "계정을 찾을 수 없습니다.",
        "account.scam_warning_title": "사기 경고",
        "account.scam_warning_description": "이 지갑 주소는 커뮤니티 보고에 따라 안전하지 않은 것으로 표시되었습니다. 사기, 개인 키 손상 또는 무단 액세스와 관련이 있을 수 있습니다. 거래를 하기 전에 주의하세요.",
        "account.overview": "개요",
        "account.available_balance": "사용 가능한 잔액",
        "account.last_modified_time": "마지막 수정 시간",
        "account.locking": "잠금",
        "account.total_locking": "총 잠금 ({{count}}개 항목)",
        "account.next_unlock_time": "다음 잠금 해제 시간",
        "account.claimable": "수령 가능",
        "account.amount": "금액",
        "account.number_of_items": "항목 수",
        "account.over_100_items": "100개 이상 항목",
        "account.tx_hash": "트랜잭션 해시",
        "account.operation_count": "작업 수",
        "account.fee": "수수료",
        "account.created_at": "생성 시간",
        "account.account": "계정",
        "account.effect": "효과",
        "account.sponsor": "스폰서",
        "account.predicate": "조건",
        "account.create_time": "생성 시간",
        "account.latest_100_claimable": "최근 100개 수령 가능 잔액",
        "account.tabs.operations": "작업",
        "account.tabs.transactions": "트랜잭션",
        "account.tabs.effects": "효과",
        "account.tabs.claimable_balances": "수령 가능 잔액",
        "effects.account_credited": "계정 입금",
        "effects.account_debited": "계정 출금",
        "effects.account_created": "계정 생성",
        "effects.claimable_balance_created": "수령 가능 잔액 생성",
        "effects.claimable_balance_claimed": "수령 가능 잔액 수령됨",
        "effects.claimable_balance_claimant_created": "수령 가능 잔액 수령자 생성",
        "effects.account_removed": "계정 삭제됨",
        "effects.account_thresholds_updated": "계정 임계값 업데이트됨",
        "effects.account_home_domain_updated": "계정 홈 도메인 업데이트됨",
        "effects.account_flags_updated": "계정 플래그 업데이트됨",
        "effects.account_inflation_destination_updated": "계정 인플레이션 대상 업데이트됨",
        "effects.signer_created": "서명자 생성됨",
        "effects.signer_removed": "서명자 제거됨",
        "effects.signer_updated": "서명자 업데이트됨",
        "effects.trustline_created": "신뢰선 생성됨",
        "effects.trustline_removed": "신뢰선 제거됨",
        "effects.trustline_updated": "신뢰선 업데이트됨",
        "effects.trustline_authorized": "신뢰선 승인됨",
        "effects.trustline_deauthorized": "신뢰선 승인 취소됨",
        "effects.offer_created": "제안 생성됨",
        "effects.offer_removed": "제안 제거됨",
        "effects.offer_updated": "제안 업데이트됨",
        "effects.trade": "거래 실행됨",
        "effects.data_created": "데이터 생성됨",
        "effects.data_removed": "데이터 제거됨",
        "effects.data_updated": "데이터 업데이트됨",
        "effects.sequence_bumped": "시퀀스 증가됨",
        "effects.liquidity_pool_created": "유동성 풀 생성됨",
        "effects.liquidity_pool_removed": "유동성 풀 제거됨",
        "effects.liquidity_pool_deposited": "유동성 풀 입금됨",
        "effects.liquidity_pool_withdrew": "유동성 풀 출금됨",
        "effects.liquidity_pool_trade": "유동성 풀 거래",
        "effects.liquidity_pool_revoked": "유동성 풀 취소됨",
        "effects.contract_credited": "계약 입금됨",
        "effects.contract_debited": "계약 출금됨",
        "effects.claimable_balance_sponsorship_removed": "수령 가능 잔액 스폰서십 제거됨",
        "account_stats.heading": "Pi 계정 통계 - 잔액 분포 및 상위 계정",
        "account_stats.title": "Pi 계정 통계 - 잔액 분포 및 상위 계정",
        "account_stats.description": "Pi 네트워크에서 가장 부유한 Pi 계정과 전반적인 잔액 분포를 확인하세요.",
        "account_stats.intro_text": "모든 계정에 걸친 Pi의 분포를 탐색하고 상위 Pi 보유자를 확인하세요.",
        "account_stats.distribution_chart_title": "Pi 보유자 분포",
        "account_stats.categories_title": "분포 카테고리",
        "account_stats.top_accounts_title": "상위 Pi 계정",
        "account_stats.accounts": "계정",
        "account_stats.of_total": "전체 중",
        "account_stats.table.category": "카테고리",
        "account_stats.table.balance_range": "잔액 범위",
        "account_stats.table.usd_value": "USD 가치",
        "account_stats.table.accounts": "계정",
        "account_stats.table.percentage": "전체 중 %",
        "account_stats.table.total": "합계",
        "account_stats.table.rank": "순위",
        "account_stats.table.account": "계정",
        "account_stats.table.balance": "잔액",
        "account_stats.table.value": "가치",
        "account_stats.table.supply_percentage": "공급량 중 %",
        "account_stats.categories.whales": "고래",
        "account_stats.categories.sharks": "상어",
        "account_stats.categories.dolphins": "돌고래",
        "account_stats.categories.tunas": "참치",
        "account_stats.categories.fish": "물고기",
        "account_stats.categories.shrimps": "새우",
        "account_stats.categories.plankton": "플랑크톤",
        "account_stats.categories.microbes": "미생물",
        "number.billion_short": "십억",
        "number.million_short": "백만",
        "number.thousand_short": "천",
        "common.loading": "로딩 중...",
        "common.sponsored": "스폰서",
        "common.ad": "광고",
        "common.ad_not_available": "귀하의 지역에서 스폰서 콘텐츠를 이용할 수 없습니다",
        "blocks.heading": "Pi 네트워크 블록체인의 최신 블록 탐색",
        "blocks.title": "Pi 네트워크의 최신 블록",
        "blocks.description": "Pi 네트워크 블록체인의 최신 블록을 탐색하세요.",
        "blocks.sequence": "시퀀스",
        "blocks.hash": "해시",
        "blocks.transactions": "트랜잭션",
        "blocks.operations": "작업",
        "blocks.time": "시간",
        "blocks.successful": "성공",
        "blocks.failed": "실패",
        "block.title": "블록 #{{sequence}} - Pi 네트워크 탐색기",
        "block.heading": "블록 #{{sequence}}",
        "block.description": "Pi 네트워크 블록체인의 블록 #{{sequence}} 상세 정보.",
        "block.loading": "블록 상세 정보 로딩 중...",
        "block.not_found": "블록을 찾을 수 없습니다.",
        "block.information": "블록 정보",
        "block.hash": "해시",
        "block.previous_hash": "이전 해시",
        "block.sequence": "시퀀스",
        "block.time": "시간",
        "block.total_coins": "총 코인",
        "block.fee_pool": "수수료 풀",
        "block.base_fee": "기본 수수료",
        "block.base_reserve": "기본 예비금",
        "block.protocol_version": "프로토콜 버전",
        "block.tx_hash": "트랜잭션 해시",
        "block.operation_count": "작업 수",
        "block.fee": "수수료",
        "block.created_at": "생성 시간",
        "block.tabs.transactions": "트랜잭션",
        "block.tabs.operations": "작업",
        "block.tabs.effects": "효과",
        "cex.heading": "CEX 및 코어 팀 계정 통계",
        "cex.title": "Pi 네트워크의 CEX 및 코어 팀 계정",
        "cex.description": "Pi 네트워크 블록체인의 CEX 및 코어 팀 계정을 탐색하세요.",
        "cex.wallet_balances": "CEX 지갑 잔액",
        "cex.core_team_wallets": "Pi 코어 팀 지갑",
        "cex.cmc_ranking": "CMC 순위",
        "cex.exchange": "거래소",
        "cex.chart_link": "차트 링크",
        "cex.signup_link": "가입 링크",
        "cex.wallet_address": "지갑 주소",
        "cex.balance_pi": "잔액 (Pi)",
        "cex.balance_usd": "잔액 (USD)",
        "cex.chart": "차트",
        "cex.signup": "가입",
        "cex.total": "총합",
        "cex.note": "참고",
        "cex.pi_foundation": "Pi 재단",
        "cex.team_wallet_footnote": "다량의 계정과 매우 적은 거래로 인해 여기에 나열되지 않은 Pi 코어 팀에 속하는 것으로 레이블이 지정된 약 10,000개의 계정이 있습니다. 각 지갑은 약 2,000,000 Pi를 보유하고 있으며, 총 약 200억 Pi가 계산되지 않았습니다.",
        'cex.total_in': '총 입금 (최근 24시간)',
'cex.total_out': '총 출금 (최근 24시간)',
'cex.net_difference': '순 흐름 (최근 24시간)',
'cex.flow_data_updated': '데이터는 30분마다 갱신됩니다',
'cex.flow_note': '최근 24시간 기준 데이터입니다. 30분마다 자동 갱신됩니다.',
        "contact.heading": "문의하기",
        "contact.title": "PiScan에 문의하기",
        "contact.description": "질문이나 피드백이 있으시면 PiScan에 문의하세요.",
        "contact.intro_text": "질문이 있으시면 다음 주소로 연락해 주세요",
        "contact.success_message": "문의해 주셔서 감사합니다! 곧 답변 드리겠습니다.",
        "contact.form.name": "이름",
        "contact.form.email": "이메일",
        "contact.form.message": "메시지",
        "contact.form.submit": "메시지 보내기",
        "home.heading": "Pi 네트워크 거래, 지갑 및 통계 탐색",
        "home.title": "Pi 네트워크 거래, 지갑 및 통계 탐색",
        "home.description": "Pi 네트워크 거래, 지갑 및 통계 탐색",
        "home.market_cap": "시가총액",
        "home.price": "가격",
        "home.24h_change": "24시간 변동",
        "home.pi_supply": "Pi 공급량",
        "home.circulating": "유통량",
        "home.locked": "잠금량",
        "home.cex_balance_total": "CEX 잔액 (총)",
        "home.view_all": "모두 보기",
        "home.network_stats": "네트워크 통계",
        "home.blocks": "블록",
        "home.tps": "TPS",
        "home.latest_transactions": "최근 거래",
        "home.signature": "서명",
        "home.time": "시간",
        "home.block": "블록",
        "home.ops": "작업 수",
        "home.latest_operations": "최근 작업",
        "home.source_account": "소스 계정",
        "home.operation": "작업",
        "home.amount": "금액",
        "home.tx": "거래",
        "nodes.heading": "Pi 네트워크 노드 맵 및 네트워크 통계",
        "nodes.title": "Pi 네트워크 노드 및 검증자",
        "nodes.description": "Pi 네트워크 노드, 검증자 및 네트워크 토폴로지를 탐색하세요. 노드의 전 세계 분포 및 연결성을 확인하세요.",
        "nodes.active_nodes": "활성 노드",
        "nodes.validators": "검증자",
        "nodes.known_peers": "알려진 피어",
        "nodes.disconnected_nodes": "연결 끊긴 노드",
        "nodes.network_graph": "네트워크 그래프",
        "nodes.node_types": "노드 유형",
        "nodes.validator": "검증자",
        "nodes.watcher": "관찰자",
        "nodes.disconnected_private": "연결 끊김/비공개",
        "nodes.click_to_highlight": "노드를 클릭하여 연결을 강조 표시",
        "nodes.global_distribution": "전 세계 분포",
        "nodes.mapped_nodes": "지도에 표시된 노드",
        "nodes.node_details": "노드 세부 정보",
        "nodes.nodes": "노드",
        "nodes.address": "주소",
        "nodes.type": "유형",
        "nodes.location": "위치",
        "nodes.isp": "인터넷 서비스 제공업체",
        "nodes.version": "버전",
        "nodes.trust": "신뢰",
        "nodes.trusted_by": "신뢰 수",
        "nodes.status": "상태",
        "nodes.status.connected": "연결됨",
        "nodes.status.disconnected": "연결 끊김",
        "nodes.status.private": "비공개",
        "nodes.active_quorum_sets": "활성 쿼럼 세트",
        "nodes.active": "활성",
        "nodes.quorum_hash": "쿼럼 해시",
        "nodes.node_id": "노드 ID",
        "nodes.unknown": "알 수 없음",
        "nodes.core_team": "코어 팀",
        "nodes.tooltip.node_id": "노드 ID",
        "nodes.tooltip.address": "주소",
        "nodes.tooltip.type": "유형",
        "nodes.tooltip.status": "상태",
        "nodes.tooltip.version": "버전",
        "nodes.tooltip.location": "위치",
        "nodes.tooltip.not_accessible": "접근 불가",
        "report_scam.heading": "사기 지갑 신고",
        "report_scam.title": "PiScan에서 사기 지갑 신고",
        "report_scam.description": "사기 또는 부정 행위와 관련된 지갑 주소를 신고하세요.",
        "report_scam.intro_text": "사기 또는 부정 행위와 관련된 의심스러운 지갑 주소를 발견하셨다면, 아래 양식을 사용하여 신고해 주세요. 저희 팀이 제출 내용을 검토하고, 사기로 확인되면 커뮤니티에 경고하기 위해 PiScan에서 해당 지갑을 \"사기\"로 표시할 것입니다.",
        "report_scam.success_message": "신고해 주셔서 감사합니다! 저희 팀이 가능한 한 빨리 검토하겠습니다.",
        "report_scam.form.wallet_address": "지갑 주소",
        "report_scam.form.wallet_placeholder": "사기 지갑 주소를 입력하세요",
        "report_scam.form.reason": "신고 이유",
        "report_scam.form.reason_placeholder": "이 지갑이 사기 활동으로 의심되는 이유를 설명하세요",
        "report_scam.form.email": "이메일 (선택사항)",
        "report_scam.form.email_placeholder": "이메일을 입력하세요 (선택사항)",
        "report_scam.form.email_note": "추가 정보를 위해 연락드릴 수 있습니다.",
        "report_scam.form.submit": "신고 제출",
        "transaction.title": "Pi 네트워크의 트랜잭션 #{{id}}",
        "transaction.heading": "트랜잭션 세부정보",
        "transaction.description": "Pi 네트워크 블록체인의 트랜잭션 #{{id}} 세부정보입니다.",
        "transaction.loading": "트랜잭션 세부정보를 로드 중...",
        "transaction.not_found": "트랜잭션을 찾을 수 없습니다.",
        "transaction.hash": "해시",
        "transaction.ledger_block": "원장 (블록)",
        "transaction.result": "결과",
        "transaction.source_account": "소스 계정",
        "transaction.fee_account": "수수료 계정",
        "transaction.fee": "수수료",
        "transaction.operation_count": "작업 수",
        "transaction.summary": "요약",
        "transaction.operations": "작업",
        "transaction.effects": "효과",
        "transaction.account": "계정",
        "transaction.operation": "작업",
        "transaction.effect": "효과",
        "transaction.amount": "금액",
        "transaction.successful": "성공",
        "transaction.failed": "실패",
        "transaction.loading_details": "트랜잭션 세부정보를 로드 중...",
        "transaction.with_operations": "{{count}}개 작업이 포함된 트랜잭션",
        "transaction.core_team_distributed": "Pi 코어 팀이 배포함",
        "transaction.to": "대상",
        "transaction.accounts": "계정",
        "transaction.locked_for_days": ", {{days}}일 동안 잠금됨",
        "transaction.locked_range_days": ", 잠금 기간은 {{minDays}}일에서 {{maxDays}}일까지 다양함",
        "transaction.created_at": "생성 시간",
        "unlock_stats.heading": "Pi 잠금 해제 통계 - 향후 30일",
        "unlock_stats.title": "Pi 잠금 해제 통계 - 향후 30일",
        "unlock_stats.description": "향후 30일 동안의 예상 Pi 암호화폐 잠금 해제 통계를 확인하세요.",
        "unlock_stats.intro_text": "향후 30일 동안의 Pi 잠금 해제는 사용자가 잠근 것이나 Pi 코어 팀이 배포한 수령 가능 잔액을 기반으로 합니다. 아직 수령 가능 잔액에 포함되지 않은 미공개 Pi는 추적할 수 없습니다. 정기적으로 업데이트를 확인하세요. 모든 시간은 UTC입니다.",
        "unlock_stats.today_note": "오늘의 잠금 해제 금액은 지금부터 하루가 끝날 때까지(UTC) 잠금 해제될 남은 Pi를 나타냅니다.",
        "unlock_stats.updated_at": "데이터 업데이트:",
        "unlock_stats.unknown": "알 수 없음",
        "unlock_stats.total_locked": "총 잠긴 Pi",
        "unlock_stats.total_next_30": "향후 30일 총 잠금 해제",
        "unlock_stats.of_total_locked": "총 잠긴 금액의",
        "unlock_stats.avg_daily": "일일 평균 잠금 해제",
        "unlock_stats.per_day": "일일",
        "unlock_stats.highest_day": "최고 일자",
        "unlock_stats.on": "날짜",
        "unlock_stats.chart_title": "Pi 잠금 해제 차트 - 향후 30일",
        "unlock_stats.data_table": "잠금 해제 데이터 테이블",
        "unlock_stats.date": "날짜",
        "unlock_stats.amount": "금액",
        "unlock_stats.value": "가치",
        "unlock_stats.pi_unlock": "Pi 잠금 해제",
        "unlock_stats.y_axis_label": "Pi 잠금 해제 (백만 단위)",
        "unlock_stats.monthly_heading": "월별 Pi 언락 통계",
        "unlock_stats.monthly_title": "월별 Pi 언락 통계",
        "unlock_stats.total_next_12": "향후 12개월 총 잠금 해제",
        "unlock_stats.monthly_description": "월별 Pi 암호화폐 언락 통계를 확인하세요",
        "unlock_stats.monthly_intro_text": "월별 Pi 암호화폐 언락 통계를 확인하세요.",
        "unlock_stats.monthly_note": "통계는 유효한 언락 요청을 기준으로 합니다.",
        "unlock_stats.monthly_chart_title": "월별 Pi 언락 통계 차트",
        "unlock_stats.total_forecast": "총 통계",
        "unlock_stats.monthly_data_table": "월별 언락 데이터",
        "unlock_stats.total_locking": "총 잠금 수량",
        "unlock_stats.avg_monthly": "월평균",
        "unlock_stats.highest_month": "최고 언락 월",
        "unlock_stats.per_month": "월별",
        "unlock_stats.in": "내",
        "unlock_stats.month": "월",
        "unlock_stats.view_daily": "일별 언락 통계 보기",
        "unlock_stats.view_monthly": "월별 언락 통계 보기",
        "common.loading_error": "데이터를 로드하는 중 오류가 발생했습니다. 나중에 다시 시도해 주세요.",
        'settings.aria_label': '설정 열기',
        'settings.language': '언어',
        'settings.appearance': '외관',
        'settings.dark_mode': '다크 모드',
        'settings.light': '라이트',
        'settings.dark': '다크',

        "transactios.heading": "Pi 네트워크 블록체인의 최신 거래 탐색",
        "transactions.title": "Pi 네트워크의 최신 거래",
        "transactions.description": "Pi 네트워크 블록체인의 최신 거래를 탐색하세요.",
          "domain_auction_stats.heading": "Pi 도메인 경매 통계",
          "domain_auction_stats.title": "Pi 도메인 경매 통계 | Pi 네트워크 통계",
          "domain_auction_stats.description": "Pi 네트워크 도메인 경매 분석, 다양한 가격대별 입찰 분포 및 총 입찰된 Pi를 표시합니다.",
          "domain_auction_stats.intro_text": "이 페이지는 Pi 도메인 경매에 대한 분석을 제공하며, 다양한 가격대별 입찰 분포를 강조합니다.",
          "domain_auction_stats.last_updated": "최종 업데이트",
          "domain_auction_stats.summary_title": "경매 입찰 요약",
          "domain_auction_stats.total_bids": "총 입찰 수",
          "domain_auction_stats.total_pi_bid": "총 입찰된 Pi",
          "domain_auction_stats.estimated_value": "예상 가치 (USD)",
          "domain_auction_stats.pi_price": "현재 Pi 가격",
          "domain_auction_stats.distribution_title": "입찰 분포",
          "domain_auction_stats.chart_title": "입찰 가격 분포",
          "domain_auction_stats.details_title": "실시간 입찰 내역",
          "domain_auction_stats.bids": "입찰",
          "domain_auction_stats.of_total": "총 입찰 중",
          "domain_auction_stats.bid_count": "입찰 수",
          "domain_auction_stats.ranges.low": "낮은 입찰",
          "domain_auction_stats.ranges.medium": "일반 입찰",
          "domain_auction_stats.ranges.high": "고급 입찰",
          "domain_auction_stats.ranges.premium": "프리미엄 입찰",
          "domain_auction_stats.ranges.elite": "최상급 입찰",
          "domain_auction_stats.table.price_range": "입찰 가격 범위",
          "domain_auction_stats.table.bid_count": "입찰 수",
          "domain_auction_stats.table.percentage": "비율",
          "domain_auction_stats.table.total": "총 입찰",
          "domain_auction_stats.highest_bid": "최고 입찰가",
          "domain_auction_stats.top_bids_title": "최고 입찰 목록",
          "domain_auction_stats.table.account": "계정",
          "domain_auction_stats.table.bid_amount": "입찰 금액",
          "domain_auction_stats.table.usd_value": "USD 가치",      
          "largest_tx.heading": "최대 Pi 거래 (최근 24시간)",
  "largest_tx.title": "최근 24시간 동안의 최대 Pi 거래 - PiScan",
  "largest_tx.description": "최근 24시간 동안 Pi Network 블록체인에서 가장 큰 Pi 거래를 탐색하세요.",
  "largest_tx.list_heading": "거래량 기준 상위 거래 (최근 24시간)",
  "largest_tx.rank": "순위",
  "largest_tx.transaction": "거래",
  "largest_tx.account": "계정",
  "largest_tx.amount": "수량 (π)",
  "largest_tx.date": "날짜 & 시간",
  "largest_tx.no_data": "최근 24시간 동안 거래 데이터가 없습니다.",
  "largest_tx.fetch_error": "거래 데이터를 불러올 수 없습니다.",
  "largest_tx.loading": "최대 거래 로딩 중...",
  "largest_tx.largest_amount": "최대 거래 금액 (최근 24시간)",
  "largest_tx.latest_large_tx": "최근 대규모 거래 (최근 24시간)",
  "largest_tx.total_value": "총 가치 (상위 100개 거래, 최근 24시간)",

  "migrate_stats.heading": "Pi 이동 통계",
  "migrate_stats.title": "Pi 메인넷 마이그레이션 통계 - PiScan",
  "migrate_stats.description": "최근 며칠 동안의 Pi 메인넷 이동 통계를 추적하세요.",
  "migrate_stats.last_updated": "마지막 업데이트",

  "migrate_stats.daily_migration": "일일 Pi 이동량",
  "migrate_stats.daily_stats": "일별 이동 통계",
  "migrate_stats.loading": "이동 통계 로딩 중...",
  "migrate_stats.no_data": "이동 데이터가 없습니다.",
  "migrate_stats.fetch_error": "이동 데이터를 불러올 수 없습니다.",
  "migrate_stats.date": "날짜",
  "migrate_stats.amount": "수량 (π)",
  "migrate_stats.percentage": "비율 (%)",
  "migrate_stats.total": "총계",
  "migrate_stats.pi_migrated": "총 이동된 Pi",
  "migrate_stats.date_label": "날짜: {{date}}",
  "migrate_stats.usd_value": "USD 가치",
  "migrate_stats.at_price": "현재 가격 기준",
  "migrate_stats.current_price_note": "USD 가치는 현재 Pi 가격 {{price}} π 당 기준으로 계산됩니다.",
      },      
      zh: {
        "nav.home": "首页",
        "nav.cexs": "CEXs 和核心团队",
        "nav.operations": "操作",
        "nav.transactions": "交易",
        "nav.blocks": "区块",
        "nav.nodes": "节点",
        "nav.stats": "统计",
        "stats.accounts": "账户分布",
        "stats.unlocks": "Pi 解锁 (30 天)",
        "stats.monthly_unlocks": "Pi 解锁 (月度)",
        'stats.domain_auction': '域名拍卖',
         "stats.largest_tx": "最大交易",
  "stats.migrate": "主网迁移统计",
        "search.placeholder": "搜索...",
        'operations.self_transfer': '自我转账',
        'operations.domain_auction': '域名拍卖',
        "operations.claim_claimable_balance": "领取 π",
        "operations.create_account": "创建账户",
        "operations.payment": "支付",
        "operations.path_payment_strict_receive": "严格接收路径支付",
        "operations.path_payment_strict_send": "严格发送路径支付",
        "operations.manage_sell_offer": "管理卖出报价",
        "operations.create_passive_sell_offer": "创建被动卖出报价",
        "operations.set_options": "设置选项",
        "operations.change_trust": "更改信任",
        "operations.allow_trust": "允许信任",
        "operations.account_merge": "账户合并",
        "operations.inflation": "通货膨胀",
        "operations.manage_data": "管理数据",
        "operations.bump_sequence": "提升序列",
        "operations.manage_buy_offer": "管理买入报价",
        "operations.create_passive_buy_offer": "创建被动买入报价",
        "operations.create_claimable_balance": "创建可领取余额",
        "operations.claim_claimable_balance_atom": "领取余额",
        "operations.deposit": "存入",
        "operations.withdraw": "提取",
        "operations.pay": "支付",
        "operations.to": "至",
        "operations.from": "从",
        "operations.claimant": "领取人",
        "operations.with_starting_balance": "起始余额为",
        "operations.claim": "领取",
        "operations.heading": "探索 Pi 网络区块链上的最新操作",
        "operations.title": "Pi 网络上的最新操作",
        "operations.description": "探索 Pi 网络区块链上的最新操作。",
        "operations.source_account": "源账户",
        "operations.operation": "操作",
        "operations.amount": "数量",
        "operations.tx": "交易",
        "operations.time": "时间",
        "pagination.previous": "上一页",
        "pagination.next": "下一页",
        "pagination.page": "页码",
        "account.title": "账户 {{address}} - Pi 网络浏览器",
        "account.heading": "账户详情",
        "account.heading_with_address": "账户 {{address}}",
        "account.description": "Pi 网络上账户 {{address}} 的详细信息。",
        "account.loading": "加载账户详情中...",
        "account.not_found": "找不到账户。",
        "account.scam_warning_title": "诈骗警告",
        "account.scam_warning_description": "根据社区报告，此钱包地址已被标记为不安全。它可能与诈骗、私钥泄露或未授权访问有关。在进行任何交易前请谨慎行事。",
        "account.overview": "概览",
        "account.available_balance": "可用余额",
        "account.last_modified_time": "最后修改时间",
        "account.locking": "锁定中",
        "account.total_locking": "总锁定 ({{count}} 项)",
        "account.next_unlock_time": "下次解锁时间",
        "account.claimable": "可领取",
        "account.amount": "金额",
        "account.number_of_items": "项目数量",
        "account.over_100_items": "超过100项",
        "account.tx_hash": "交易哈希",
        "account.operation_count": "操作数量",
        "account.fee": "费用",
        "account.created_at": "创建时间",
        "account.account": "账户",
        "account.effect": "效果",
        "account.sponsor": "赞助商",
        "account.predicate": "条件",
        "account.create_time": "创建时间",
        "account.latest_100_claimable": "最新100个可领取余额",
        "account.tabs.operations": "操作",
        "account.tabs.transactions": "交易",
        "account.tabs.effects": "效果",
        "account.tabs.claimable_balances": "可领取余额",
        "effects.account_credited": "账户已入账",
        "effects.account_debited": "账户已扣款",
        "effects.account_created": "账户已创建",
        "effects.claimable_balance_created": "可领取余额已创建",
        "effects.claimable_balance_claimed": "可领取余额已领取",
        "effects.claimable_balance_claimant_created": "可领取余额领取人已创建",
        "effects.account_removed": "账户已删除",
        "effects.account_thresholds_updated": "账户阈值已更新",
        "effects.account_home_domain_updated": "账户主域名已更新",
        "effects.account.flags_updated": "账户标志已更新",
        "effects.account_inflation_destination_updated": "账户通胀目标已更新",
        "effects.signer_created": "签署人已创建",
        "effects.signer_removed": "签署人已删除",
        "effects.signer.updated": "签署人已更新",
        "effects.trustline_created": "信任线已创建",
        "effects.trustline_removed": "信任线已删除",
        "effects.trustline.updated": "信任线已更新",
        "effects.trustline_authorized": "信任线已授权",
        "effects.trustline.deauthorized": "信任线已取消授权",
        "effects.offer_created": "报价已创建",
        "effects.offer_removed": "报价已删除",
        "effects.offer.updated": "报价已更新",
        "effects.trade": "交易已执行",
        "effects.data.created": "数据已创建",
        "effects.data.removed": "数据已删除",
        "effects.data.updated": "数据已更新",
        "effects.sequence_bumped": "序列已提升",
        "effects.liquidity_pool_created": "流动性池已创建",
        "effects.liquidity_pool_removed": "流动性池已删除",
        "effects.liquidity_pool_deposited": "流动性池已存入",
        "effects.liquidity_pool_withdrew": "流动性池已提取",
        "effects.liquidity_pool_trade": "流动性池交易",
        "effects.liquidity_pool_revoked": "流动性池已撤销",
        "effects.contract_credited": "合约已入账",
        "effects.contract_debited": "合约已扣款",
        "effects.claimable_balance_sponsorship_removed": "可领取余额赞助已移除",
        "account_stats.heading": "Pi 账户统计 - 余额分布和顶级账户",
        "account_stats.title": "Pi 账户统计 - 余额分布和顶级账户",
        "account_stats.description": "查看 Pi 网络上最富有的 Pi 账户和整体余额分布。",
        "account_stats.intro_text": "探索所有账户中 Pi 的分布并查看顶级 Pi 持有者。",
        "account_stats.distribution_chart_title": "Pi 持有者分布",
        "account_stats.categories_title": "分布类别",
        "account_stats.top_accounts_title": "顶级 Pi 账户",
        "account_stats.accounts": "账户",
        "account_stats.of_total": "占总数",
        "account_stats.table.category": "类别",
        "account_stats.table.balance_range": "余额范围",
        "account_stats.table.usd_value": "美元价值",
        "account_stats.table.accounts": "账户数",
        "account_stats.table.percentage": "总数百分比",
        "account_stats.table.total": "总计",
        "account_stats.table.rank": "排名",
        "account_stats.table.account": "账户",
        "account_stats.table.balance": "余额",
        "account_stats.table.value": "价值",
        "account_stats.table.supply_percentage": "总供应量百分比",
        "account_stats.categories.whales": "鲸鱼",
        "account_stats.categories.sharks": "鲨鱼",
        "account_stats.categories.dolphins": "海豚",
        "account_stats.categories.tunas": "金枪鱼",
        "account_stats.categories.fish": "鱼",
        "account_stats.categories.shrimps": "虾",
        "account_stats.categories.plankton": "浮游生物",
        "account_stats.categories.microbes": "微生物",
        "number.billion_short": "亿",
        "number.million_short": "百万",
        "number.thousand_short": "千",
        "common.loading": "加载中...",
        "common.sponsored": "赞助",
        "common.ad": "广告",
        "common.ad_not_available": "您所在地区无法提供赞助内容",
        "blocks.heading": "探索 Pi 网络区块链上的最新区块",
        "blocks.title": "Pi 网络上的最新区块",
        "blocks.description": "探索 Pi 网络区块链上的最新区块。",
        "blocks.sequence": "序列",
        "blocks.hash": "哈希",
        "blocks.transactions": "交易",
        "blocks.operations": "操作",
        "blocks.time": "时间",
        "blocks.successful": "成功",
        "blocks.failed": "失败",
        "block.title": "区块 #{{sequence}} - Pi 网络浏览器",
        "block.heading": "区块 #{{sequence}}",
        "block.description": "Pi 网络区块链上区块 #{{sequence}} 的详细信息。",
        "block.loading": "加载区块详情中...",
        "block.not_found": "找不到区块。",
        "block.information": "区块信息",
        "block.hash": "哈希",
        "block.previous_hash": "前序哈希",
        "block.sequence": "序列",
        "block.time": "时间",
        "block.total_coins": "总币量",
        "block.fee_pool": "手续费池",
        "block.base_fee": "基础手续费",
        "block.base_reserve": "基础准备金",
        "block.protocol_version": "协议版本",
        "block.tx_hash": "交易哈希",
        "block.operation_count": "操作数量",
        "block.fee": "手续费",
        "block.created_at": "创建时间",
        "block.tabs.transactions": "交易",
        "block.tabs.operations": "操作",
        "block.tabs.effects": "效果",
        "cex.heading": "CEX 和核心团队账户统计",
        "cex.title": "Pi 网络上的 CEX 和核心团队账户",
        "cex.description": "探索 Pi 网络区块链上的 CEX 和核心团队账户。",
        "cex.wallet_balances": "CEX 钱包余额",
        "cex.core_team_wallets": "Pi 核心团队钱包",
        "cex.cmc_ranking": "CMC 排名",
        "cex.exchange": "交易所",
        "cex.chart_link": "图表链接",
        "cex.signup_link": "注册链接",
        "cex.wallet_address": "钱包地址",
        "cex.balance_pi": "余额 (Pi)",
        "cex.balance_usd": "余额 (USD)",
        "cex.chart": "图表",
        "cex.signup": "注册",
        "cex.total": "总计",
        "cex.note": "备注",
        "cex.pi_foundation": "Pi 基金会",
        "cex.team_wallet_footnote": "由于数量庞大且交易极少，这里未列出约 10,000 个标记为属于 Pi 核心团队的账户。每个钱包持有约 2,000,000 Pi，总计约 200 亿 Pi 尚未计入。",
       'cex.total_in': '总流入（近24小时）',
'cex.total_out': '总流出（近24小时）',
'cex.net_difference': '净流量（近24小时）',
'cex.flow_data_updated': '数据每30分钟更新一次',
'cex.flow_note': '基于近24小时数据，每30分钟自动更新。',

        "contact.heading": "联系我们",
        "contact.title": "联系 PiScan",
        "contact.description": "如有任何疑问或反馈，请联系 PiScan。",
        "contact.intro_text": "如果您有任何问题，请随时通过以下方式联系我们",
        "contact.success_message": "感谢您与我们联系！我们将尽快回复您。",
        "contact.form.name": "您的姓名",
        "contact.form.email": "您的电子邮箱",
        "contact.form.message": "消息",
        "contact.form.submit": "发送消息",
        "home.heading": "探索 Pi 网络交易、钱包和统计数据",
        "home.title": "探索 Pi 网络交易、钱包和统计数据",
        "home.description": "探索 Pi 网络交易、钱包和统计数据",
        "home.market_cap": "市值",
        "home.price": "价格",
        "home.24h_change": "24小时变化",
        "home.pi_supply": "Pi 供应量",
        "home.circulating": "流通量",
        "home.locked": "锁定量",
        "home.cex_balance_total": "CEX 余额 (总计)",
        "home.view_all": "查看全部",
        "home.network_stats": "网络统计",
        "home.blocks": "区块",
        "home.tps": "TPS",
        "home.latest_transactions": "最新交易",
        "home.signature": "签名",
        "home.time": "时间",
        "home.block": "区块",
        "home.ops": "操作",
        "home.latest_operations": "最新操作",
        "home.source_account": "源账户",
        "home.operation": "操作",
        "home.amount": "数量",
        "home.tx": "交易",
        "nodes.heading": "Pi 网络节点地图和网络统计",
        "nodes.title": "Pi 网络节点和验证者",
        "nodes.description": "探索 Pi 网络节点、验证者和网络拓扑。查看节点的全球分布和连接性。",
        "nodes.active_nodes": "活跃节点",
        "nodes.validators": "验证者",
        "nodes.known_peers": "已知对等点",
        "nodes.disconnected_nodes": "断开连接节点",
        "nodes.network_graph": "网络图",
        "nodes.node_types": "节点类型",
        "nodes.validator": "验证者",
        "nodes.watcher": "观察者",
        "nodes.disconnected_private": "断开连接/私有",
        "nodes.click_to_highlight": "点击节点以突出显示其连接",
        "nodes.global_distribution": "全球分布",
        "nodes.mapped_nodes": "已映射节点",
        "nodes.node_details": "节点详情",
        "nodes.nodes": "节点",
        "nodes.address": "地址",
        "nodes.type": "类型",
        "nodes.location": "位置",
        "nodes.isp": "互联网服务提供商",
        "nodes.version": "版本",
        "nodes.trust": "信任",
        "nodes.trusted_by": "被信任",
        "nodes.status": "状态",
        "nodes.status.connected": "已连接",
        "nodes.status.disconnected": "已断开",
        "nodes.status.private": "私有",
        "nodes.active_quorum_sets": "活跃法定人数集",
        "nodes.active": "活跃",
        "nodes.quorum_hash": "法定人数哈希",
        "nodes.node_id": "节点 ID",
        "nodes.unknown": "未知",
        "nodes.core_team": "核心团队",
        "nodes.tooltip.node_id": "节点 ID",
        "nodes.tooltip.address": "地址",
        "nodes.tooltip.type": "类型",
        "nodes.tooltip.status": "状态",
        "nodes.tooltip.version": "版本",
        "nodes.tooltip.location": "位置",
        "nodes.tooltip.not_accessible": "无法访问",
        "report_scam.heading": "举报诈骗钱包",
        "report_scam.title": "在 PiScan 上举报诈骗钱包",
        "report_scam.description": "举报涉及欺诈或诈骗活动的钱包地址。",
        "report_scam.intro_text": "如果您遇到了涉及欺诈或诈骗活动的可疑钱包地址，请使用以下表格进行举报。我们的团队将审核您的提交，如果确认为诈骗，我们将在 PiScan 上将该钱包标记为“诈骗”，以警示社区。",
        "report_scam.success_message": "感谢您的举报！我们的团队将尽快审核。",
        "report_scam.form.wallet_address": "钱包地址",
        "report_scam.form.wallet_placeholder": "输入诈骗钱包地址",
        "report_scam.form.reason": "举报原因",
        "report_scam.form.reason_placeholder": "描述为什么怀疑此钱包涉及诈骗活动",
        "report_scam.form.email": "您的电子邮箱（可选）",
        "report_scam.form.email_placeholder": "输入您的电子邮箱（可选）",
        "report_scam.form.email_note": "我们可能会联系您了解更多详情。",
        "report_scam.form.submit": "提交举报",
        "transaction.title": "Pi 网络上的交易 #{{id}}",
        "transaction.heading": "交易详情",
        "transaction.description": "Pi 网络区块链上交易 #{{id}} 的详细信息。",
        "transaction.loading": "正在加载交易详情...",
        "transaction.not_found": "未找到交易。",
        "transaction.hash": "哈希值",
        "transaction.ledger_block": "账本（区块）",
        "transaction.result": "结果",
        "transaction.source_account": "源账户",
        "transaction.fee_account": "手续费账户",
        "transaction.fee": "手续费",
        "transaction.operation_count": "操作数量",
        "transaction.summary": "摘要",
        "transaction.operations": "操作",
        "transaction.effects": "效果",
        "transaction.account": "账户",
        "transaction.operation": "操作",
        "transaction.effect": "效果",
        "transaction.amount": "金额",
        "transaction.successful": "成功",
        "transaction.failed": "失败",
        "transaction.loading_details": "正在加载交易详情...",
        "transaction.with_operations": "包含 {{count}} 个操作的交易",
        "transaction.core_team_distributed": "Pi 核心团队分发了",
        "transaction.to": "给",
        "transaction.accounts": "账户",
        "transaction.locked_for_days": "，锁定 {{days}} 天",
        "transaction.locked_range_days": "，锁定期从 {{minDays}} 天到 {{maxDays}} 天不等",
        "transaction.created_at": "创建时间",
        "unlock_stats.heading": "Pi 解锁统计 - 未来 30 天",
        "unlock_stats.title": "Pi 解锁统计 - 未来 30 天",
        "unlock_stats.description": "查看未来 30 天的 Pi 加密货币解锁统计预测。",
        "unlock_stats.intro_text": "未来 30 天的 Pi 解锁基于可领取余额——可能是用户锁定的或由 Pi 核心团队分配的。尚未包含在可领取余额中的未发布 Pi 无法追踪。请定期查看更新。所有时间均为 UTC。",
        "unlock_stats.today_note": "今天的解锁金额代表从现在到当天结束（UTC）将要解锁的剩余 Pi。",
        "unlock_stats.updated_at": "数据更新于：",
        "unlock_stats.unknown": "未知",
        "unlock_stats.total_locked": "总锁定 Pi",
        "unlock_stats.total_next_30": "未来 30 天总解锁量",
        "unlock_stats.of_total_locked": "占总锁定量",
        "unlock_stats.avg_daily": "平均每日解锁量",
        "unlock_stats.per_day": "每天",
        "unlock_stats.highest_day": "最高解锁日",
        "unlock_stats.on": "在",
        "unlock_stats.chart_title": "Pi 解锁图表 - 未来 30 天",
        "unlock_stats.data_table": "解锁数据表",
        "unlock_stats.date": "日期",
        "unlock_stats.amount": "金额",
        "unlock_stats.value": "价值",
        "unlock_stats.pi_unlock": "Pi 解锁",
        "unlock_stats.y_axis_label": "Pi 解锁（百万计）",
        "unlock_stats.monthly_heading": "每月Pi解锁统计",
        "unlock_stats.monthly_title": "每月Pi解锁统计",
        "unlock_stats.total_next_12": "未来 12 个月总解锁量",
        "unlock_stats.monthly_description": "查看每月Pi加密货币解锁统计",
        "unlock_stats.monthly_intro_text": "查看按月统计的Pi加密货币解锁情况。",
        "unlock_stats.monthly_note": "统计基于符合条件的解锁请求。",
        "unlock_stats.monthly_chart_title": "每月Pi解锁统计图表",
        "unlock_stats.total_forecast": "总统计",
        "unlock_stats.monthly_data_table": "每月解锁数据",
        "unlock_stats.total_locking": "总锁定量",
        "unlock_stats.avg_monthly": "每月平均",
        "unlock_stats.highest_month": "最高月份",
        "unlock_stats.per_month": "每月",
        "unlock_stats.in": "于",
        "unlock_stats.month": "月份",
        "unlock_stats.view_daily": "查看每日解锁统计",
        "unlock_stats.view_monthly": "查看每月解锁统计",
        "common.loading_error": "加载数据时出错。请稍后再试。",
        'settings.aria_label': '打开设置',
        'settings.language': '语言',
        'settings.appearance': '外观',
        'settings.dark_mode': '深色模式',
        'settings.light': '浅色',
        'settings.dark': '深色',

        "transactios.heading": "探索 Pi 网络区块链上的最新交易",
        "transactions.title": "Pi 网络上的最新交易",
        "transactions.description": "探索 Pi 网络区块链上的最新交易。",

        "domain_auction_stats.heading": "Pi 域名拍卖统计",
    "domain_auction_stats.title": "Pi 域名拍卖统计 | Pi 网络统计",
    "domain_auction_stats.description": "分析 Pi 网络域名拍卖，显示不同价格区间的出价分布以及目前已出价的 Pi 总额。",
    "domain_auction_stats.intro_text": "本页面提供 Pi 域名拍卖分析，重点展示不同价格区间的出价分布。",
    "domain_auction_stats.last_updated": "最后更新",
    "domain_auction_stats.summary_title": "拍卖出价概览",
    "domain_auction_stats.total_bids": "总出价次数",
    "domain_auction_stats.total_pi_bid": "已出价 Pi 总额",
    "domain_auction_stats.estimated_value": "预估价值 (USD)",
    "domain_auction_stats.pi_price": "当前 Pi 价格",
    "domain_auction_stats.distribution_title": "出价分布",
    "domain_auction_stats.chart_title": "出价价格分布",
    "domain_auction_stats.details_title": "实时出价明细",
    "domain_auction_stats.bids": "出价",
    "domain_auction_stats.of_total": "占总出价",
    "domain_auction_stats.bid_count": "出价次数",
    "domain_auction_stats.ranges.low": "低额出价",
    "domain_auction_stats.ranges.medium": "标准出价",
    "domain_auction_stats.ranges.high": "高级出价",
    "domain_auction_stats.ranges.premium": "尊享出价",
    "domain_auction_stats.ranges.elite": "顶级出价",
    "domain_auction_stats.table.price_range": "出价区间",
    "domain_auction_stats.table.bid_count": "出价次数",
    "domain_auction_stats.table.percentage": "占比",
    "domain_auction_stats.table.total": "总出价",
    "domain_auction_stats.highest_bid": "最高出价",
    "domain_auction_stats.top_bids_title": "最高出价列表",
    "domain_auction_stats.table.account": "账户",
    "domain_auction_stats.table.bid_amount": "出价金额",
    "domain_auction_stats.table.usd_value": "美元价值",

   "largest_tx.heading": "最大 Pi 交易（过去 24 小时）",
  "largest_tx.title": "过去 24 小时内最大 Pi 交易 - PiScan",
  "largest_tx.description": "探索过去 24 小时 Pi Network 区块链上的最大 Pi 交易。",
  "largest_tx.list_heading": "按交易金额排名（过去 24 小时）",
  "largest_tx.rank": "排名",
  "largest_tx.transaction": "交易",
  "largest_tx.account": "账户",
  "largest_tx.amount": "金额 (π)",
  "largest_tx.date": "日期 & 时间",
  "largest_tx.no_data": "过去 24 小时内无交易数据。",
  "largest_tx.fetch_error": "无法获取交易数据。",
  "largest_tx.loading": "正在加载最大交易...",
  "largest_tx.largest_amount": "最大交易金额（过去 24 小时）",
  "largest_tx.latest_large_tx": "最新大额交易（过去 24 小时）",
  "largest_tx.total_value": "总价值（前 100 笔交易，过去 24 小时）",

  "migrate_stats.heading": "Pi 迁移统计",
  "migrate_stats.title": "Pi 迁移至主网统计 - PiScan",
  "migrate_stats.description": "跟踪最近几天 Pi 迁移至主网的统计数据。",
  "migrate_stats.last_updated": "最后更新",

  "migrate_stats.daily_migration": "每日迁移量",
  "migrate_stats.daily_stats": "每日迁移统计",
  "migrate_stats.loading": "正在加载迁移统计...",
  "migrate_stats.no_data": "暂无迁移数据。",
  "migrate_stats.fetch_error": "无法获取迁移数据。",
  "migrate_stats.date": "日期",
  "migrate_stats.amount": "金额 (π)",
  "migrate_stats.percentage": "百分比 (%)",
  "migrate_stats.total": "总计",
  "migrate_stats.pi_migrated": "已迁移总 Pi",
  "migrate_stats.date_label": "日期：{{date}}",
  "migrate_stats.usd_value": "美元价值",
  "migrate_stats.at_price": "当前价格",
  "migrate_stats.current_price_note": "美元价值按当前 Pi 价格 {{price}} 每 π 计算。",
      },
      tr:  {
        "nav.home": "Ana Sayfa",
        "nav.cexs": "CEX'ler ve Çekirdek Takım",
        "nav.operations": "Operasyonlar",
        "nav.transactions": "İşlemler",
        "nav.blocks": "Bloklar",
        "nav.nodes": "Düğümler",
        "nav.stats": "İstatistikler",
        "stats.accounts": "Hesap Dağılımı",
        "stats.unlocks": "Pi Kilidini Açma (30 Gün)",
        "stats.monthly_unlocks": "Pi Kilidini Açma (Aylık)",
        'stats.domain_auction': 'Alan Adı Müzayedesi',
        "stats.largest_tx": "En Büyük İşlem",
  "stats.migrate": "Ana Ağ Geçiş İstatistikleri",
        "search.placeholder": "Ara...",
        'operations.self_transfer': 'Kendi Hesabına Transfer',
        'operations.domain_auction': 'Alan Adı Müzayedesi',
        "operations.claim_claimable_balance": "π Talep Et",
        "operations.create_account": "Hesap Oluştur",
        "operations.payment": "Ödeme",
        "operations.path_payment_strict_receive": "Sıkı Alım Yolu ile Ödeme",
        "operations.path_payment_strict_send": "Sıkı Gönderim Yolu ile Ödeme",
        "operations.manage_sell_offer": "Satış Teklifini Yönet",
        "operations.create_passive_sell_offer": "Pasif Satış Teklifi Oluştur",
        "operations.set_options": "Seçenekleri Ayarla",
        "operations.change_trust": "Güveni Değiştir",
        "operations.allow_trust": "Güvene İzin Ver",
        "operations.account_merge": "Hesap Birleştirme",
        "operations.inflation": "Enflasyon",
        "operations.manage_data": "Verileri Yönet",
        "operations.bump_sequence": "Sıralamayı Artır",
        "operations.manage_buy_offer": "Alım Teklifini Yönet",
        "operations.create_passive_buy_offer": "Pasif Alım Teklifi Oluştur",
        "operations.create_claimable_balance": "Talep Edilebilir Bakiye Oluştur",
        "operations.claim_claimable_balance_atom": "Talep Edilebilir Bakiyeyi Talep Et",
        "operations.deposit": "Yatır",
        "operations.withdraw": "Çek",
        "operations.pay": "Öde",
        "operations.to": "Alıcı",
        "operations.from": "Gönderen",
        "operations.claimant": "Talep Sahibi",
        "operations.with_starting_balance": "Başlangıç Bakiyesi Dahil",
        "operations.claim": "Talep Et",
        "operations.heading": "Pi Ağ Blok Zincirindeki En Son Operasyonları Keşfedin",
        "operations.title": "Pi Ağındaki En Son Operasyonlar",
        "operations.description": "Pi Ağ blok zincirindeki en son operasyonları keşfedin.",
        "operations.source_account": "Kaynak Hesap",
        "operations.operation": "Operasyon",
        "operations.amount": "Miktar",
        "operations.tx": "İşlem",
        "operations.time": "Zaman",
        "pagination.previous": "Önceki",
        "pagination.next": "Sonraki",
        "pagination.page": "Sayfa",
        "account.title": "Hesap {{address}} - Pi Ağ Tarayıcısı",
        "account.heading": "Hesap Detayları",
        "account.heading_with_address": "Hesap {{address}}",
        "account.description": "Pi Ağ üzerindeki {{address}} hesabının detayları.",
        "account.loading": "Hesap detayları yükleniyor...",
        "account.not_found": "Hesap bulunamadı.",
        "account.scam_warning_title": "Dolandırıcılık Uyarısı",
        "account.scam_warning_description": "Topluluk raporlarına göre, bu cüzdan adresi güvenli değil olarak işaretlenmiştir. Dolandırıcılık, özel anahtar ihlali veya yetkisiz erişim ile ilişkili olabilir. İşlem yapmadan önce dikkatli olun.",
        "account.overview": "Genel Bakış",
        "account.available_balance": "Kullanılabilir Bakiye",
        "account.last_modified_time": "Son Değişiklik Zamanı",
        "account.locking": "Kilitli",
        "account.total_locking": "Toplam Kilitlenme ({{count}} öğe)",
        "account.next_unlock_time": "Sonraki Kilidin Açılma Zamanı",
        "account.claimable": "Talep Edilebilir",
        "account.amount": "Miktar",
        "account.number_of_items": "Öğe Sayısı",
        "account.over_100_items": "100'den Fazla Öğe",
        "account.tx_hash": "İşlem Hash'i",
        "account.operation_count": "Operasyon Sayısı",
        "account.fee": "Ücret",
        "account.created_at": "Oluşturulma Zamanı",
        "account.account": "Hesap",
        "account.effect": "Etkisi",
        "account.sponsor": "Sponsor",
        "account.predicate": "Koşul",
        "account.create_time": "Oluşturulma Zamanı",
        "account.latest_100_claimable": "En Son 100 Talep Edilebilir Bakiye",
        "account.tabs.operations": "Operasyonlar",
        "account.tabs.transactions": "İşlemler",
        "account.tabs.effects": "Etkiler",
        "account.tabs.claimable_balances": "Talep Edilebilir Bakiyeler",
        "effects.account_credited": "Hesap Alacaklandırıldı",
        "effects.account_debited": "Hesap Borçlandırıldı",
        "effects.account_created": "Hesap Oluşturuldu",
        "effects.claimable_balance_created": "Talep Edilebilir Bakiye Oluşturuldu",
        "effects.claimable_balance_claimed": "Talep Edilebilir Bakiye Talep Edildi",
        "effects.claimable_balance_claimant_created": "Talep Edilebilir Bakiye Talep Sahibi Oluşturuldu",
        "effects.account_removed": "Hesap Silindi",
        "effects.account_thresholds_updated": "Hesap Eşik Değerleri Güncellendi",
        "effects.account_home_domain_updated": "Hesap Ana Domaini Güncellendi",
        "effects.account.flags_updated": "Hesap Bayrakları Güncellendi",
        "effects.account_inflation_destination_updated": "Hesap Enflasyon Hedefi Güncellendi",
        "effects.signer_created": "İmza Yetkilisi Oluşturuldu",
        "effects.signer_removed": "İmza Yetkilisi Kaldırıldı",
        "effects.signer.updated": "İmza Yetkilisi Güncellendi",
        "effects.trustline_created": "Güven Hattı Oluşturuldu",
        "effects.trustline_removed": "Güven Hattı Kaldırıldı",
        "effects.trustline.updated": "Güven Hattı Güncellendi",
        "effects.trustline_authorized": "Güven Hattı Yetkilendirildi",
        "effects.trustline.deauthorized": "Güven Hattı Yetkilendirmesi Kaldırıldı",
        "effects.offer_created": "Teklif Oluşturuldu",
        "effects.offer_removed": "Teklif Kaldırıldı",
        "effects.offer.updated": "Teklif Güncellendi",
        "effects.trade": "Ticaret Gerçekleşti",
        "effects.data.created": "Veri Oluşturuldu",
        "effects.data.removed": "Veri Silindi",
        "effects.data.updated": "Veri Güncellendi",
        "effects.sequence_bumped": "Sıra Artırıldı",
        "effects.liquidity_pool_created": "Likidite Havuzu Oluşturuldu",
        "effects.liquidity_pool_removed": "Likidite Havuzu Kaldırıldı",
        "effects.liquidity_pool_deposited": "Likidite Havuzuna Para Yatırıldı",
        "effects.liquidity_pool_withdrew": "Likidite Havuzundan Para Çekildi",
        "effects.liquidity_pool_trade": "Likidite Havuzu Ticareti",
        "effects.liquidity_pool_revoked": "Likidite Havuzu İptal Edildi",
        "effects.contract_credited": "Sözleşme Alacaklandırıldı",
        "effects.contract_debited": "Sözleşme Borçlandırıldı",
        "effects.claimable_balance_sponsorship_removed": "Talep Edilebilir Bakiye Sponsorluğu Kaldırıldı",
        "account_stats.heading": "Pi Hesap İstatistikleri - Bakiye Dağılımı ve En Üst Hesaplar",
        "account_stats.title": "Pi Hesap İstatistikleri - Bakiye Dağılımı ve En Üst Hesaplar",
        "account_stats.description": "Pi ağındaki en zengin Pi hesaplarını ve genel bakiye dağılımını görün.",
        "account_stats.intro_text": "Tüm hesaplardaki Pi dağılımını keşfedin ve en üst Pi sahiplerini görün.",
        "account_stats.distribution_chart_title": "Pi Sahip Dağılımı",
        "account_stats.categories_title": "Dağılım Kategorileri",
        "account_stats.top_accounts_title": "En Üst Pi Hesapları",
        "account_stats.accounts": "hesaplar",
        "account_stats.of_total": "toplamın",
        "account_stats.table.category": "Kategori",
        "account_stats.table.balance_range": "Bakiye Aralığı",
        "account_stats.table.usd_value": "USD Değeri",
        "account_stats.table.accounts": "Hesap Sayısı",
        "account_stats.table.percentage": "Toplam Yüzdesi",
        "account_stats.table.total": "Toplam",
        "account_stats.table.rank": "Sıra",
        "account_stats.table.account": "Hesap",
        "account_stats.table.balance": "Bakiye",
        "account_stats.table.value": "Değer",
        "account_stats.table.supply_percentage": "Toplam Arz Yüzdesi",
        "account_stats.categories.whales": "Balinalar",
        "account_stats.categories.sharks": "Köpekbalıkları",
        "account_stats.categories.dolphins": "Yunuslar",
        "account_stats.categories.tunas": "Ton balıkları",
        "account_stats.categories.fish": "Balıklar",
        "account_stats.categories.shrimps": "Karidesler",
        "account_stats.categories.plankton": "Plankton",
        "account_stats.categories.microbes": "Mikroorganizmalar",
        "number.billion_short": "Milyar",
        "number.million_short": "Milyon",
        "number.thousand_short": "Bin",
        "common.loading": "Yükleniyor...",
        "common.sponsored": "Sponsorlu",
        "common.ad": "Reklam",
        "common.ad_not_available": "Bölgenizde sponsorlu içerik mevcut değil",
        "blocks.heading": "Pi Ağ Blok Zincirindeki En Son Blokları Keşfedin",
        "blocks.title": "Pi Ağındaki En Son Bloklar",
        "blocks.description": "Pi ağ blok zincirindeki en son blokları keşfedin.",
        "blocks.sequence": "Sıra",
        "blocks.hash": "Hash",
        "blocks.transactions": "İşlemler",
        "blocks.operations": "Operasyonlar",
        "blocks.time": "Zaman",
        "blocks.successful": "Başarılı",
        "blocks.failed": "Başarısız",
        "block.title": "Blok #{{sequence}} - Pi Ağ Tarayıcısı",
        "block.heading": "Blok #{{sequence}}",
        "block.description": "Pi ağ blok zincirindeki Blok #{{sequence}} detayları.",
        "block.loading": "Blok detayları yükleniyor...",
        "block.not_found": "Blok bulunamadı.",
        "block.information": "Blok Bilgisi",
        "block.hash": "Hash",
        "block.previous_hash": "Önceki Hash",
        "block.sequence": "Sıra",
        "block.time": "Zaman",
        "block.total_coins": "Toplam Coin",
        "block.fee_pool": "Ücret Havuzu",
        "block.base_fee": "Temel Ücret",
        "block.base_reserve": "Temel Rezerv",
        "block.protocol_version": "Protokol Sürümü",
        "block.tx_hash": "İşlem Hash'i",
        "block.operation_count": "Operasyon Sayısı",
        "block.fee": "Ücret",
        "block.created_at": "Oluşturulma Zamanı",
        "block.tabs.transactions": "İşlemler",
        "block.tabs.operations": "Operasyonlar",
        "block.tabs.effects": "Etkiler",
        "cex.heading": "CEX ve Çekirdek Takım Hesap İstatistikleri",
        "cex.title": "Pi Ağındaki CEX ve Çekirdek Takım Hesapları",
        "cex.description": "Pi ağ blok zincirindeki CEX ve çekirdek takım hesaplarını keşfedin.",
        "cex.wallet_balances": "CEX Cüzdan Bakiyeleri",
        "cex.core_team_wallets": "Pi Çekirdek Takım Cüzdanları",
        "cex.cmc_ranking": "CMC Sıralaması",
        "cex.exchange": "Borsa",
        "cex.chart_link": "Grafik Bağlantısı",
        "cex.signup_link": "Kayıt Bağlantısı",
        "cex.wallet_address": "Cüzdan Adresi",
        "cex.balance_pi": "Bakiye (Pi)",
        "cex.balance_usd": "Bakiye (USD)",
        "cex.chart": "Grafik",
        "cex.signup": "Kayıt Ol",
        "cex.total": "Toplam",
        "cex.note": "Not",
        "cex.pi_foundation": "Pi Vakfı",
        "cex.team_wallet_footnote": "Çok sayıda hesap ve çok az işlem nedeniyle, burada Pi çekirdek takımına ait olduğu işaretlenmiş yaklaşık 10.000 hesap listelenmemiştir. Her cüzdan yaklaşık 2.000.000 Pi tutmakta olup, toplamda yaklaşık 20 milyar Pi hesaplanmamıştır.",
       'cex.total_in': 'Toplam Giriş (son 24 saat)',
'cex.total_out': 'Toplam Çıkış (son 24 saat)',
'cex.net_difference': 'Net Akış (son 24 saat)',
'cex.flow_data_updated': 'Veriler her 30 dakikada bir güncellenir',
'cex.flow_note': 'Veriler son 24 saati kapsamaktadır ve her 30 dakikada bir otomatik olarak güncellenir.',
        "contact.heading": "Bize Ulaşın",
        "contact.title": "PiScan ile İletişime Geçin",
        "contact.description": "Herhangi bir sorunuz veya geri bildiriminiz varsa, lütfen PiScan ile iletişime geçin.",
        "contact.intro_text": "Sorularınız varsa, aşağıdaki yolla bizimle iletişime geçin",
        "contact.success_message": "Bize ulaştığınız için teşekkürler! En kısa sürede size dönüş yapacağız.",
        "contact.form.name": "Adınız",
        "contact.form.email": "E-posta Adresiniz",
        "contact.form.message": "Mesaj",
        "contact.form.submit": "Mesaj Gönder",
        "home.heading": "Pi Ağ İşlemlerini, Cüzdanlarını ve İstatistiklerini Keşfedin",
        "home.title": "Pi Ağ İşlemlerini, Cüzdanlarını ve İstatistiklerini Keşfedin",
        "home.description": "Pi ağ işlemlerini, cüzdanlarını ve istatistiklerini keşfedin",
        "home.market_cap": "Piyasa Değeri",
        "home.price": "Fiyat",
        "home.24h_change": "24 Saat Değişim",
        "home.pi_supply": "Pi Arzı",
        "home.circulating": "Dolaşımdaki",
        "home.locked": "Kilitli",
        "home.cex_balance_total": "CEX Bakiyesi (Toplam)",
        "home.view_all": "Tümünü Gör",
        "home.network_stats": "Ağ İstatistikleri",
        "home.blocks": "Bloklar",
        "home.tps": "TPS",
        "home.latest_transactions": "En Son İşlemler",
        "home.signature": "İmza",
        "home.time": "Zaman",
        "home.block": "Blok",
        "home.ops": "Operasyonlar",
        "home.latest_operations": "En Son Operasyonlar",
        "home.source_account": "Kaynak Hesap",
        "home.operation": "Operasyon",
        "home.amount": "Miktar",
        "home.tx": "İşlem",
        "nodes.heading": "Pi Ağ Düğümler Haritası ve Ağ İstatistikleri",
        "nodes.title": "Pi Ağ Düğümleri ve Doğrulayıcılar",
        "nodes.description": "Pi ağ düğümlerini, doğrulayıcıları ve ağ topolojisini keşfedin. Düğümlerin küresel dağılımını ve bağlantılarını görün.",
        "nodes.active_nodes": "Aktif Düğümler",
        "nodes.validators": "Doğrulayıcılar",
        "nodes.known_peers": "Bilinen Eşler",
        "nodes.disconnected_nodes": "Bağlantısı Kesilmiş Düğümler",
        "nodes.network_graph": "Ağ Grafiği",
        "nodes.node_types": "Düğüm Türleri",
        "nodes.validator": "Doğrulayıcı",
        "nodes.watcher": "İzleyici",
        "nodes.disconnected_private": "Bağlantısı Kesilmiş/Özel",
        "nodes.click_to_highlight": "Bağlantıları vurgulamak için düğüme tıklayın",
        "nodes.global_distribution": "Küresel Dağılım",
        "nodes.mapped_nodes": "Haritalanmış Düğümler",
        "nodes.node_details": "Düğüm Detayları",
        "nodes.nodes": "Düğümler",
        "nodes.address": "Adres",
        "nodes.type": "Tür",
        "nodes.location": "Konum",
        "nodes.isp": "İnternet Servis Sağlayıcısı",
        "nodes.version": "Sürüm",
        "nodes.trust": "Güven",
        "nodes.trusted_by": "Güvenilen",
        "nodes.status": "Durum",
        "nodes.status.connected": "Bağlı",
        "nodes.status.disconnected": "Bağlantısı Kesilmiş",
        "nodes.status.private": "Özel",
        "nodes.active_quorum_sets": "Aktif Oy Birliği Setleri",
        "nodes.active": "Aktif",
        "nodes.quorum_hash": "Oy Birliği Hash'i",
        "nodes.node_id": "Düğüm ID'si",
        "nodes.unknown": "Bilinmiyor",
        "nodes.core_team": "Çekirdek Takım",
        "nodes.tooltip.node_id": "Düğüm ID'si",
        "nodes.tooltip.address": "Adres",
        "nodes.tooltip.type": "Tür",
        "nodes.tooltip.status": "Durum",
        "nodes.tooltip.version": "Sürüm",
        "nodes.tooltip.location": "Konum",
        "nodes.tooltip.not_accessible": "Erişilemiyor",
        "report_scam.heading": "Dolandırıcılık Cüzdanını Bildir",
        "report_scam.title": "PiScan'de Dolandırıcılık Cüzdanını Bildir",
        "report_scam.description": "Dolandırıcılık veya sahtekarlık faaliyetiyle ilişkili cüzdan adresini bildirin.",
        "report_scam.intro_text": "Dolandırıcılık veya sahtekarlık faaliyetiyle ilişkili şüpheli bir cüzdan adresiyle karşılaştıysanız, lütfen aşağıdaki formu doldurarak bildirin. Ekibimiz gönderinizi inceleyecek ve dolandırıcılık olarak doğrulanırsa, topluluğu uyarmak amacıyla PiScan'de ilgili cüzdanı 'Dolandırıcılık' olarak işaretleyecektir.",
        "report_scam.success_message": "Bildiriminiz için teşekkürler! Ekibimiz en kısa sürede inceleyecektir.",
        "report_scam.form.wallet_address": "Cüzdan Adresi",
        "report_scam.form.wallet_placeholder": "Dolandırıcılık cüzdan adresini girin",
        "report_scam.form.reason": "Bildirim Nedeni",
        "report_scam.form.reason_placeholder": "Bu cüzdanın neden dolandırıcılıkla ilişkili olduğuna dair açıklama yapın",
        "report_scam.form.email": "E-posta Adresiniz (Opsiyonel)",
        "report_scam.form.email_placeholder": "E-posta adresinizi girin (opsiyonel)",
        "report_scam.form.email_note": "Daha fazla bilgi için sizinle iletişime geçebiliriz.",
        "report_scam.form.submit": "Bildirimi Gönder",
        "transaction.title": "Pi Ağındaki İşlem #{{id}}",
        "transaction.heading": "İşlem Detayları",
        "transaction.description": "Pi ağ blok zincirindeki İşlem #{{id}} detayları.",
        "transaction.loading": "İşlem detayları yükleniyor...",
        "transaction.not_found": "İşlem bulunamadı.",
        "transaction.hash": "Hash",
        "transaction.ledger_block": "Defter (Blok)",
        "transaction.result": "Sonuç",
        "transaction.source_account": "Kaynak Hesap",
        "transaction.fee_account": "Ücret Hesabı",
        "transaction.fee": "Ücret",
        "transaction.operation_count": "Operasyon Sayısı",
        "transaction.summary": "Özet",
        "transaction.operations": "Operasyonlar",
        "transaction.effects": "Etkiler",
        "transaction.account": "Hesap",
        "transaction.operation": "Operasyon",
        "transaction.effect": "Etki",
        "transaction.amount": "Miktar",
        "transaction.successful": "Başarılı",
        "transaction.failed": "Başarısız",
        "transaction.loading_details": "İşlem detayları yükleniyor...",
        "transaction.with_operations": "{{count}} adet operasyon içeren işlem",
        "transaction.core_team_distributed": "Pi Çekirdek Takım tarafından dağıtıldı",
        "transaction.to": "Alıcı",
        "transaction.accounts": "Hesaplar",
        "transaction.locked_for_days": ", {{days}} gün kilitli",
        "transaction.locked_range_days": ", kilit süresi {{minDays}} günden {{maxDays}} güne kadar değişiyor",
        "transaction.created_at": "Oluşturulma Zamanı",
        "unlock_stats.heading": "Pi Kilidini Açma İstatistikleri - Önümüzdeki 30 Gün",
        "unlock_stats.title": "Pi Kilidini Açma İstatistikleri - Önümüzdeki 30 Gün",
        "unlock_stats.description": "Önümüzdeki 30 gün için Pi kripto paranın kilidini açma tahminlerini görün.",
        "unlock_stats.intro_text": "Önümüzdeki 30 gündeki Pi kilidinin açılması, talep edilebilir bakiyeye dayanmaktadır – bu bakiye kullanıcı tarafından kilitlenmiş veya Pi Çekirdek Takım tarafından dağıtılmış olabilir. Henüz talep edilebilir bakiyeye dahil olmayan yayınlanmamış Pi takip edilemez. Lütfen düzenli olarak güncellemeleri kontrol edin. Tüm zamanlar UTC'dir.",
        "unlock_stats.today_note": "Bugünkü kilidini açma miktarı, şuan itibariyle gün sonuna (UTC) kadar açılacak kalan Pi'yi temsil eder.",
        "unlock_stats.updated_at": "Veri güncellendi:",
        "unlock_stats.unknown": "Bilinmiyor",
        "unlock_stats.total_locked": "Toplam Kilitli Pi",
        "unlock_stats.total_next_30": "Önümüzdeki 30 Gün İçin Toplam Kilidini Açma",
        "unlock_stats.of_total_locked": "toplam kilitli miktarın",
        "unlock_stats.avg_daily": "Ortalama Günlük Kilidini Açma",
        "unlock_stats.per_day": "günlük",
        "unlock_stats.highest_day": "En Yüksek Kilidini Açma Günü",
        "unlock_stats.on": "tarihinde",
        "unlock_stats.chart_title": "Pi Kilidini Açma Grafiği - Önümüzdeki 30 Gün",
        "unlock_stats.data_table": "Kilidini Açma Veri Tablosu",
        "unlock_stats.date": "Tarih",
        "unlock_stats.amount": "Miktar",
        "unlock_stats.value": "Değer",
        "unlock_stats.pi_unlock": "Pi Kilidini Açma",
        "unlock_stats.y_axis_label": "Pi Kilidini Açma (milyon cinsinden)",
        "unlock_stats.monthly_heading": "Aylık Pi Kilit Açma İstatistikleri",
        "unlock_stats.monthly_title": "Aylık Pi Kilit Açma İstatistikleri",
        "unlock_stats.total_next_12": "Önümüzdeki 12 Ay İçin Toplam Kilidini Açma",
        "unlock_stats.monthly_description": "Aylara göre Pi kripto para kilit açma istatistiklerini görüntüleyin",
        "unlock_stats.monthly_intro_text": "Aylık Pi kripto para kilit açma istatistiklerini görüntüleyin.",
        "unlock_stats.monthly_note": "İstatistikler uygun kilit açma taleplerine dayanmaktadır.",
        "unlock_stats.monthly_chart_title": "Aylık Pi Kilit Açma İstatistikleri Grafiği",
        "unlock_stats.total_forecast": "Toplam İstatistik",
        "unlock_stats.monthly_data_table": "Aylık Kilit Açma Verileri",
        "unlock_stats.total_locking": "Toplam Kilitlenen Miktar",
        "unlock_stats.avg_monthly": "Aylık Ortalama",
        "unlock_stats.highest_month": "En Yüksek Ay",
        "unlock_stats.per_month": "ay başına",
        "unlock_stats.in": "içinde",
        "unlock_stats.month": "Ay",
        "unlock_stats.view_daily": "Günlük Kilit Açma İstatistiklerini Görüntüle",
        "unlock_stats.view_monthly": "Aylık Kilit Açma İstatistiklerini Görüntüle",
        "common.loading_error": "Veriler yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.",

        "transactios.heading": "Pi Ağı Blok Zincirindeki En Son İşlemleri Keşfedin",
        "transactions.title": "Pi Ağındaki En Son İşlemler",
        "transactions.description": "Pi ağ blok zincirindeki en son işlemleri keşfedin.",
        "domain_auction_stats.heading": "Pi Alan Adı Açık Artırma İstatistikleri",
    "domain_auction_stats.title": "Pi Alan Adı Açık Artırma İstatistikleri | Pi Ağ İstatistikleri",
    "domain_auction_stats.description": "Pi Ağı alan adı açık artırmalarının analizini gösterir, farklı fiyat aralıklarındaki teklif dağılımını ve şimdiye kadar teklif edilen toplam Pi miktarını sunar.",
    "domain_auction_stats.intro_text": "Bu sayfa, Pi alan adı açık artırmalarına ilişkin analizler sunar ve farklı fiyat aralıklarındaki teklif dağılımını vurgular.",
    "domain_auction_stats.last_updated": "Son Güncelleme",
    "domain_auction_stats.summary_title": "Açık Artırma Teklif Özeti",
    "domain_auction_stats.total_bids": "Toplam Teklif Sayısı",
    "domain_auction_stats.total_pi_bid": "Şimdiye Kadar Teklif Edilen Toplam Pi",
    "domain_auction_stats.estimated_value": "Tahmini Değer (USD)",
    "domain_auction_stats.pi_price": "Güncel Pi Fiyatı",
    "domain_auction_stats.distribution_title": "Teklif Dağılımı",
    "domain_auction_stats.chart_title": "Teklif Fiyat Dağılımı",
    "domain_auction_stats.details_title": "Canlı Teklif Ayrıntıları",
    "domain_auction_stats.bids": "teklifler",
    "domain_auction_stats.of_total": "toplam teklifler içinde",
    "domain_auction_stats.bid_count": "Teklif Sayısı",
    "domain_auction_stats.ranges.low": "Düşük Teklifler",
    "domain_auction_stats.ranges.medium": "Standart Teklifler",
    "domain_auction_stats.ranges.high": "Premium Teklifler",
    "domain_auction_stats.ranges.premium": "Lüks Teklifler",
    "domain_auction_stats.ranges.elite": "Elit Teklifler",
    "domain_auction_stats.table.price_range": "Teklif Fiyat Aralığı",
    "domain_auction_stats.table.bid_count": "Teklif Sayısı",
    "domain_auction_stats.table.percentage": "Yüzde",
    "domain_auction_stats.table.total": "Toplam Teklifler",
    "domain_auction_stats.highest_bid": "En Yüksek Teklif",
    "domain_auction_stats.top_bids_title": "En Yüksek Teklifler",
    "domain_auction_stats.table.account": "Hesap",
    "domain_auction_stats.table.bid_amount": "Teklif Miktarı",
    "domain_auction_stats.table.usd_value": "USD Değeri",

    "largest_tx.heading": "En Büyük Pi İşlemleri (Son 24 Saat)",
  "largest_tx.title": "Son 24 Saat İçindeki En Büyük Pi İşlemleri - PiScan",
  "largest_tx.description": "Son 24 saat içinde Pi Network blok zincirindeki en büyük Pi işlemlerini keşfedin.",
  "largest_tx.list_heading": "Miktara Göre En Büyük İşlemler (Son 24 Saat)",
  "largest_tx.rank": "Sıralama",
  "largest_tx.transaction": "İşlem",
  "largest_tx.account": "Hesap",
  "largest_tx.amount": "Miktar (π)",
  "largest_tx.date": "Tarih & Saat",
  "largest_tx.no_data": "Son 24 saat içinde işlem verisi bulunmamaktadır.",
  "largest_tx.fetch_error": "İşlem verisi alınamadı.",
  "largest_tx.loading": "En büyük işlemler yükleniyor...",
  "largest_tx.largest_amount": "En Büyük İşlem Miktarı (Son 24 Saat)",
  "largest_tx.latest_large_tx": "En Son Büyük İşlem (Son 24 Saat)",
  "largest_tx.total_value": "Toplam Değer (İlk 100 İşlem, Son 24 Saat)",

  "migrate_stats.heading": "Pi Geçiş İstatistikleri",
  "migrate_stats.title": "Pi Ana Ağa Geçiş İstatistikleri - PiScan",
  "migrate_stats.description": "Son günlerde Pi’nin ana ağa geçişine dair günlük istatistikleri takip edin.",
  "migrate_stats.last_updated": "Son Güncelleme",

  "migrate_stats.daily_migration": "Günlük Geçiş Hacmi",
  "migrate_stats.daily_stats": "Güne Göre Geçiş İstatistikleri",
  "migrate_stats.loading": "Geçiş istatistikleri yükleniyor...",
  "migrate_stats.no_data": "Geçiş verisi bulunmamaktadır.",
  "migrate_stats.fetch_error": "Geçiş verisi alınamadı.",
  "migrate_stats.date": "Tarih",
  "migrate_stats.amount": "Miktar (π)",
  "migrate_stats.percentage": "Yüzde (%)",
  "migrate_stats.total": "Toplam",
  "migrate_stats.pi_migrated": "Toplam Geçirilen Pi",
  "migrate_stats.date_label": "Tarih: {{date}}",
  "migrate_stats.usd_value": "USD Değeri",
  "migrate_stats.at_price": "mevcut fiyat üzerinden",
  "migrate_stats.current_price_note": "USD değerleri mevcut Pi fiyatı {{price}} π başına hesaplanmıştır."
      }
          
    };
    
    return translations[lang] || translations.en;
  } catch (error) {
    console.error('Failed to load translations:', error);
    return {};
  }
};

export const LanguageProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  // Get saved language or use browser language preference
  const getBrowserLanguage = (): Language => {
    const browserLang = navigator.language.split('-')[0];
    return (browserLang as Language) in languageNames ? (browserLang as Language) : 'en';
  };

  const savedLanguage = localStorage.getItem('language') as Language;
  const [language, setLanguage] = useState<Language>(savedLanguage || getBrowserLanguage());
  const [translations, setTranslations] = useState<Record<string, string>>({});
  
  // Updated translation function to handle template variables
  const t = (key: string, variables?: Record<string, any>): string => {
    let text = translations[key] || key;
    
    // Replace template variables if provided
    if (variables) {
      Object.entries(variables).forEach(([varName, value]) => {
        text = text.replace(new RegExp(`{{${varName}}}`, 'g'), String(value));
      });
    }
    
    return text;
  };

  // Set language handler
  const handleSetLanguage = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('language', lang);
  };

  // Load translations when language changes
  useEffect(() => {
    const loadLanguage = async () => {
      const trans = await loadTranslations(language);
      setTranslations(trans);
      // Set html lang attribute for accessibility
      document.documentElement.lang = language;
    };
    
    loadLanguage();
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};